import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs,
  TextInput, fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import {
  BulkTagBar, EMPTY_TAG_FILTER, LIFECYCLE_LABEL, TagFilter, type TagFilterValue, tagCell, tagFilterParams,
  toLifecycle,
} from './tags/shared';
import { AdminLink, type AdminTargetIn } from './adminNav';
import { Badge, Labeled, Modal, inputCls } from './email/emailKit';
import { fmtIST } from './email/trace/DeliveryTrace';
import { AccountAccessSection, SuspendAccountModal, describeSuspension } from './users/AccountAccessSection';
import { PlanSection } from './users/PlanSection';

/**
 * Users group — migrated from admin_panel_v2.py Tab 1 (User Management),
 * Tab 2 (Approvals & Invitations), Tab 3 (Task Queue), Tab 5 (Chat & Feedback),
 * Tab 10 (Account Deletions), the LinkedIn Profile Cache viewer and the
 * admin_v3 Users/Actions/Requests pages. The two Streamlit bulk-delete flows
 * (paste an email list / pick rows off the table) collapse into one panel that
 * takes targets from either source and posts them in a single users/bulk_delete
 * call; the duplicated LinkedIn-regenerate buttons are dropped from MVP (the
 * profile editor keeps the LinkedIn URL field).
 */

const TABS = ['Directory', 'Approvals', 'Task queue', 'Add user', 'Bulk add', 'Sign-in',
  'Send invites', 'Invitations', 'LinkedIn cache', 'Deletion requests', 'E-mail changes', 'Contact limits',
  'Feedback & bugs'];
/** A deep link's tab=: the label itself or its snake_case ("Task queue" → task_queue).
 *  A hyphen joins its word ("E-mail changes" → email_changes). */
const tabKey = (s: string) =>
  s.toLowerCase().replace(/-/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const tabFromLink = (raw: string) => TABS.find(t => t === raw || tabKey(t) === tabKey(raw));

/** The account a deep link asked the Directory to open; n re-applies it. */
type Focus = { user: string; email: string; n: string };

/**
 * Deep link (owner, 2026-10-05): /admin?group=users&user=<uuid> (or &email=…)
 * opens that account's drawer over the Directory; &tab= picks a sub-tab.
 * The bell's possible-abuse items link to tab=Contact limits&user=<uuid>,
 * which opens the drawer over that tab instead.
 */
const UsersPanel: React.FC<{ initialTab?: string; target?: AdminTargetIn }> = ({ initialTab, target }) => {
  const [tab, setTab] = useState(initialTab && TABS.includes(initialTab) ? initialTab : TABS[0]);
  const [focus, setFocus] = useState<Focus | null>(null);
  // The shell hands a sub-tab over once (the deletion banner's Review button)
  // and clears it straight after, so only a real, known tab name moves the
  // selection — the clear-back to undefined must not bounce the user around.
  useEffect(() => {
    if (initialTab && TABS.includes(initialTab)) setTab(initialTab);
  }, [initialTab]);
  useEffect(() => {
    if (!target) return;
    const user = String(target.user || '').trim();
    const email = String(target.email || '').trim();
    const t = tabFromLink(String(target.tab || '').trim()) || (user || email ? 'Directory' : undefined);
    if (t) setTab(t);
    setFocus((user || email) && (t === 'Directory' || t === 'Contact limits')
      ? { user, email, n: target.nonce } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  return (
    <div className="flex flex-col gap-4">
      {/* A tab click is a fresh start: the linked account is not re-opened later. */}
      <SubTabs tabs={TABS} active={tab} onChange={t => { setFocus(null); setTab(t); }} />
      {tab === 'Directory' && <Directory focus={focus} />}
      {tab === 'Approvals' && <Approvals onOpenTasks={() => setTab('Task queue')} />}
      {tab === 'Task queue' && <TaskQueue />}
      {tab === 'Add user' && <AddUser />}
      {tab === 'Bulk add' && <BulkAdd />}
      {tab === 'Sign-in' && <SignInSettings />}
      {tab === 'Send invites' && <SendInvites />}
      {tab === 'Invitations' && <Invitations />}
      {tab === 'LinkedIn cache' && <LinkedInCache />}
      {tab === 'Deletion requests' && <DeletionRequests />}
      {tab === 'E-mail changes' && <EmailChangeLog />}
      {tab === 'Contact limits' && <ContactLimits focus={focus} />}
      {tab === 'Feedback & bugs' && <FeedbackBugs />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------
type Flash = { kind: 'error' | 'ok'; text: string } | null;

/** Amber callout — Msg only carries the ok/error pair. */
const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
    {children}
  </div>
);

const Pill: React.FC<{ tone: 'ok' | 'warn' | 'bad' | 'mute'; children: React.ReactNode }> =
  ({ tone, children }) => {
  const look = tone === 'ok' ? 'text-green-700 bg-green-50 border-green-200'
    : tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200'
      : tone === 'bad' ? 'text-red-600 bg-red-50 border-red-200'
        : 'text-grayscale-1200 bg-grayscale-200 border-grayscale-300';
  return <span className={`text-[11px] rounded-full border px-2 py-0.5 whitespace-nowrap ${look}`}>{children}</span>;
};

/** yes/no pill for a boolean flag; undefined stays a dash (field not selected). */
const flagCell = (v: any) =>
  v === true ? <Pill tone="ok">yes</Pill>
    : v === false ? <Pill tone="bad">no</Pill>
      : <span className="text-grayscale-1100">—</span>;

const TASK_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  completed: 'ok', processing: 'warn', pending: 'mute', failed: 'bad',
};
const taskPill = (status: any) => {
  const s = String(status || '').toLowerCase();
  if (!s) return <span className="text-grayscale-1100">—</span>;
  return <Pill tone={TASK_TONE[s] || 'mute'}>{s}</Pill>;
};

const Check: React.FC<{ checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode }> =
  ({ checked, onChange, children }) => (
  <label className="flex items-start gap-2 text-xs text-grayscale-1200 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-0.5" />
    <span>{children}</span>
  </label>
);

const LinkCell = (row: Record<string, any>, key = 'linkedin_url') => (
  row[key]
    ? <a href={row[key]} target="_blank" rel="noreferrer"
         className="text-primary-700 underline">{String(row[key]).replace(/^https?:\/\/(www\.)?/, '')}</a>
    : <span className="text-grayscale-1100">—</span>
);

/** Keeps ILIKE-heavy searches off every keystroke. */
function useDebounced(value: string, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const fmtSecs = (s: any): string => {
  const n = Number(s);
  if (s === null || s === undefined || isNaN(n)) return '—';
  if (n < 60) return `${n.toFixed(1)}s`;
  return `${Math.floor(n / 60)}m ${Math.round(n % 60)}s`;
};

/**
 * users/task_config returns booleans only — never a key. When the server has no
 * BrightData/Azure or no SendGrid, an approval still creates the account but
 * silently skips enrichment or the welcome mail, so say so up front.
 */
const ConfigNotices: React.FC<{ cfg: any }> = ({ cfg }) => {
  if (!cfg) return null;
  const gaps: string[] = [];
  if (!cfg.linkedin_enabled) {
    gaps.push('LinkedIn enrichment is disabled server-side — accounts are created with an empty professional summary.');
  }
  if (!cfg.sendgrid_configured) {
    gaps.push('SendGrid is not configured — the welcome / “claim your profile” email will not be sent.');
  }
  if (!gaps.length) return null;
  return (
    <Notice>
      <b>Background workers run degraded.</b>
      <ul className="list-disc ml-4 mt-1">{gaps.map(g => <li key={g}>{g}</li>)}</ul>
    </Notice>
  );
};

// ---------------------------------------------------------------------------
/** Directory rows per page; "All" asks for up to 5,000 (the server's cap). */
const PAGE_SIZES: Array<{ label: string; n: number }> = [
  { label: '50', n: 50 }, { label: '100', n: 100 }, { label: '250', n: 250 }, { label: '500', n: 500 }, { label: 'All', n: 5000 },
];
/** Server-side cap in users/bulk_delete — mirrored so the UI can say so first. */
const BULK_LIMIT = 200;
const BULK_ACK = 'DELETE';

const BASE_COLS = ['email', 'first_name', 'last_name', 'organisation_name', 'country',
  'reg_method', 'logins', 'searches', 'saves'];
const STATUS_COLS = ['is_approved', 'is_verified', 'is_active'];
/** The drawer's on/off buttons. is_active is left out: the server refuses a
 *  bare is_active change, because suspending goes through Account access
 *  (reason, sign-out, billing) — the Directory still shows the column. */
const DRAWER_TOGGLES = STATUS_COLS.filter(c => c !== 'is_active');
/** Everything else users/list carries, off until turned on with the table's
 *  Columns picker (owner, 2026-10-05: "show whatever data is available"). */
const EXTRA_COLS = ['trial_ends_at', 'trial_status', 'trials', 'plan_code', 'plan_status', 'plan_started_at',
  'plan_ends_at', 'first_paid_at', 'last_login_at', 'last_search_at', 'last_chat_at', 'last_activity_at',
  'last_emailed_at', 'first_invited_at', 'persona', 'profile_pct', 'chats', 'active_days',
  'account_origin_label', 'account_origin_via_label', 'city', 'designation', 'linkedin_profile_url',
  'contact_flag', 'id'];
/** Directory "Account" filter → users/list account= (suspended / active /
 *  flagged = an open possible-abuse flag on university contacts). */
const ACCOUNT_FILTERS = ['All', 'active', 'suspended', 'flagged'];
const ACCOUNT_FILTER_LABEL: Record<string, string> = {
  All: 'All accounts', active: 'Active', suspended: 'Suspended', flagged: 'Flagged (possible abuse)',
};
const COL_LABELS: Record<string, string> = {
  reg_method: 'Source', organisation_name: 'Organisation',
  is_approved: 'Approved', is_verified: 'Verified', is_active: 'Active', contact_flag: 'Contact flag',
  lifecycle: 'Lifecycle · tags',
  trial_started_at: 'Trial start', trial_ends_at: 'Trial end', trial_status: 'Trial status', trials: 'Trials',
  plan_code: 'Plan', plan_status: 'Plan status', plan_started_at: 'Plan since', plan_ends_at: 'Plan renews / ends',
  first_paid_at: 'First payment', last_login_at: 'Last login', last_search_at: 'Last search',
  last_chat_at: 'Last chat', last_activity_at: 'Last activity', last_emailed_at: 'Last e-mailed',
  first_invited_at: 'First invited', persona: 'Persona', profile_pct: 'Profile %', chats: 'Chats',
  active_days: 'Active days', account_origin_label: 'Origin', account_origin_via_label: 'How the account was made',
  city: 'City', designation: 'Designation', linkedin_profile_url: 'LinkedIn', id: 'User id',
};

/** "Show our own accounts" (owner, 2026-10-05) — off by default, remembered
 *  per browser. Our own accounts are the analytics exclusion list. */
const SHOW_OWN_KEY = 'utech.admin.users.showOwnAccounts';
const readShowOwn = (): boolean => {
  try { return localStorage.getItem(SHOW_OWN_KEY) === '1'; } catch { return false; }
};

const Directory: React.FC<{ focus?: Focus | null }> = ({ focus }) => {
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('All');
  const [country, setCountry] = useState('All');
  const [account, setAccount] = useState('All');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [msg, setMsg] = useState<Flash>(null);
  const [miss, setMiss] = useState<{ who: string; error: string } | null>(null);

  // A deep-linked account: a uuid is checked with the drawer's own read; an
  // e-mail is resolved with user360/find, which (unlike users/list) also
  // finds our own accounts. Only an exact match opens the drawer.
  useEffect(() => {
    if (!focus) return;
    let live = true;
    setMiss(null);
    setSelected(null);
    (async () => {
      let id = '';
      let err = '';
      if (focus.user) {
        const { data, error } = await panelGet('users', 'detail', { user_id: focus.user });
        id = data?.user?.id ? String(data.user.id) : '';
        err = error;
      } else {
        const w = focus.email.toLowerCase();
        const { data, error } = await panelGet('user360', 'find',
          { q: focus.email, limit: 25, page: 0, exclude_internal: '' });
        const hit = (data?.rows || []).find((r: any) => String(r.email || '').toLowerCase() === w);
        id = hit ? String(hit.id) : '';
        err = error;
      }
      if (!live) return;
      if (id) setSelected(id);
      else setMiss({ who: focus.user || focus.email, error: err });
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n]);
  // Ticked rows survive paging and re-filtering, so the email is carried along
  // with the id — the row it came from may not be on screen any more.
  const [checked, setChecked] = useState<Record<string, string>>({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  // Lifecycle / tag filter — the standard params (user_tags.parse_filter),
  // applied server-side so paging and the total stay right.
  const [tf, setTf] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const tagParams = useMemo(() => tagFilterParams(tf), [tf]);
  const tagKey = JSON.stringify(tagParams);

  // Our own accounts (the analytics exclusion list) are hidden unless asked
  // for; sent explicitly as '1'/'0' to both the list and the KPI row.
  const [showOwn, setShowOwnState] = useState<boolean>(readShowOwn);
  const setShowOwn = (v: boolean) => {
    setShowOwnState(v);
    setPage(0);
    try { localStorage.setItem(SHOW_OWN_KEY, v ? '1' : '0'); } catch { /* private mode */ }
  };
  const excludeInternal = showOwn ? '0' : '1';

  const summary = useAdminGet('users', 'summary', { exclude_internal: excludeInternal }, [excludeInternal]);
  const countries = useAdminGet('users', 'countries');
  // Every account on one page by default (owner, 2026-09-29); smaller pages
  // on request.
  const [pageSize, setPageSize] = useState(5000);
  const list = useAdminGet('users', 'list',
    { search, source, country, limit: pageSize, offset: page * pageSize,
      exclude_internal: excludeInternal, ...tagParams, ...(account !== 'All' ? { account } : {}) },
    [search, source, country, account, page, tagKey, pageSize, excludeInternal]);
  const pickAccount = (v: string) => { setAccount(v); setPage(0); };

  const rows = list.data?.rows || [];
  const total = list.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const nChecked = Object.keys(checked).length;
  const pageIds: string[] = rows.map((r: any) => r.id);
  const allOnPage = pageIds.length > 0 && pageIds.every(id => checked[id]);

  // users/list does not select the status flags today; show the columns only
  // when the payload actually carries them, so the Directory starts reflecting
  // real approve/verify/active state the moment the backend adds them.
  const columns = useMemo(() => {
    const hasStatus = rows[0] ? STATUS_COLS.some(c => c in rows[0]) : false;
    const [first, ...rest] = BASE_COLS;
    return [first, 'lifecycle', ...rest, ...(hasStatus ? STATUS_COLS : []), 'created_at', 'trial_started_at'];
  }, [rows]);

  const unchecked = (ids: string[]) => setChecked(s => {
    const next = { ...s };
    ids.forEach(id => delete next[id]);
    return next;
  });

  const toggleRow = (r: any) => setChecked(s => {
    const next = { ...s };
    if (next[r.id]) delete next[r.id];
    else next[r.id] = r.email;
    return next;
  });

  const togglePage = () => {
    if (allOnPage) unchecked(pageIds);
    else {
      setChecked(s => {
        const next = { ...s };
        rows.forEach((r: any) => { next[r.id] = r.email; });
        return next;
      });
      setBulkOpen(true);
    }
  };

  const del = async (userId: string, email: string) => {
    const { data, error } = await panelPost('users', 'delete', { user_id: userId });
    if (error || !data?.success) setMsg({ kind: 'error', text: error || data?.error || 'Delete failed' });
    else {
      const n = Object.values(data.deleted_counts || {}).reduce((a: number, b: any) => a + (b || 0), 0);
      setMsg({ kind: 'ok', text: `Deleted ${email} (${n} rows across tables).` });
      setSelected(null);
      unchecked([userId]);
      list.reload();
      summary.reload();
    }
  };

  const afterBulk = (deletedIds: string[]) => {
    // Rows that failed stay ticked so they can be retried without re-finding them.
    unchecked(deletedIds);
    if (selected && deletedIds.includes(selected)) setSelected(null);
    list.reload();
    summary.reload();
  };

  return (
    <div className="flex flex-col gap-4">
      {summary.data && (
        <KPIRow>
          <KPI label="Total users" value={summary.data.total?.toLocaleString?.() ?? summary.data.total} />
          <KPI label="Ever searched" value={summary.data.active} />
          <KPI label=">1 login" value={summary.data.multi_login} />
          <KPI label="With password" value={summary.data.with_password} />
          <KPI label="Never searched" value={summary.data.zero_searches}
            hint="The activation list" />
          <KPI label="Total searches" value={summary.data.total_searches} />
          <KPI label="Zero chats" value={summary.data.zero_chats} hint="AI chat, the older feature" />
          {summary.data.suspended !== undefined && (
            <KPI label="Suspended" value={summary.data.suspended} hint="cannot sign in"
              onClick={() => pickAccount(account === 'suspended' ? 'All' : 'suspended')}
              active={account === 'suspended'} />
          )}
        </KPIRow>
      )}
      <Section title={`Directory (${total.toLocaleString()} users)`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-grayscale-1100 flex items-center gap-1.5 cursor-pointer mr-1"
              title="Our own accounts are the addresses on the analytics exclusion list (admins and team).">
              <input type="checkbox" checked={showOwn} onChange={e => setShowOwn(e.target.checked)} />
              Show our own accounts
            </label>
            <Btn small onClick={togglePage} disabled={!pageIds.length}>
              {allOnPage ? 'Unselect page' : 'Select page'}
            </Btn>
            {nChecked > 0 && <Btn small onClick={() => setChecked({})}>Clear ({nChecked})</Btn>}
            <Btn small kind={tagsOpen ? 'primary' : 'subtle'} onClick={() => setTagsOpen(o => !o)}
              title="Add or remove tags on the ticked rows">
              Add/remove tags{nChecked ? ` (${nChecked})` : ''}
            </Btn>
            <Btn small kind={bulkOpen ? 'primary' : 'subtle'} onClick={() => setBulkOpen(o => !o)}>
              Bulk delete{nChecked ? ` (${nChecked})` : ''}
            </Btn>
            <Btn small onClick={list.reload}>Refresh</Btn>
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={search} onChange={v => { setSearch(v); setPage(0); }}
            placeholder="Search email / name / organisation…" wide />
          <Select value={source} onChange={v => { setSource(v); setPage(0); }}
            options={['All', 'LinkedIn', 'Google', 'Email']} labels={{ All: 'All sources' }} />
          <Select value={country} onChange={v => { setCountry(v); setPage(0); }}
            options={['All', ...(countries.data || [])]} labels={{ All: 'All countries' }} />
          <Select value={account} onChange={pickAccount} options={ACCOUNT_FILTERS} labels={ACCOUNT_FILTER_LABEL} />
        </div>
        <TagFilter value={tf} onChange={v => { setTf(v); setPage(0); }} />
        {miss && (
          <Msg kind="error">
            {miss.error ? `Could not open ${miss.who}: ${miss.error}` : `No account for ${miss.who}.`}
            {miss.who.includes('@') && (
              <> <AdminLink to={{ group: 'email', tab: 'trace', email: miss.who }}>Trace this address</AdminLink></>
            )}
          </Msg>
        )}
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading ? <Loading /> : (
          <DataTable rows={rows} csvName="users" columns={columns} optionalColumns={EXTRA_COLS}
            labels={COL_LABELS}
            render={{
              email: r => (
                <span className="inline-flex items-center gap-1.5">
                  <span>{r.email}</span>
                  {r.is_admin && <Pill tone="warn">admin</Pill>}
                  {r.is_internal && <Pill tone="mute">team</Pill>}
                  {r.is_active === false && (
                    <span title="Suspended — cannot sign in. Reinstate from the account (Open).">
                      <Pill tone="bad">suspended</Pill>
                    </span>
                  )}
                  {r.contact_flag && clFlagPill(r.contact_flag, 'flagged')}
                </span>
              ),
              lifecycle: tagCell,
              is_approved: r => flagCell(r.is_approved),
              is_verified: r => flagCell(r.is_verified),
              is_active: r => flagCell(r.is_active),
              contact_flag: r => r.contact_flag ? clFlagPill(r.contact_flag) : <span className="text-grayscale-1100">—</span>,
            }}
            actions={r => (
              <div className="flex gap-1 items-center">
                <input type="checkbox" checked={!!checked[r.id]} onChange={() => toggleRow(r)}
                  aria-label={`Select ${r.email}`} title="Select for bulk tags / bulk delete"
                  className="mr-1 cursor-pointer" />
                <Btn small onClick={() => setSelected(r.id)}>Open</Btn>
                <DangerBtn small onConfirm={() => del(r.id, r.email)}>Delete</DangerBtn>
              </div>
            )} />
        )}
        <div className="flex items-center gap-2 justify-end text-xs text-grayscale-1100">
          <label className="flex items-center gap-1 mr-2">Rows per page
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
              className="h-7 px-1 rounded border-[0.5px] border-primary-300 bg-white">
              {PAGE_SIZES.map(o => <option key={o.n} value={o.n}>{o.label}</option>)}
            </select>
          </label>
          <Btn small disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Btn>
          Page {page + 1} of {pages}
          <Btn small disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next</Btn>
        </div>
      </Section>
      {tagsOpen && (
        <BulkTagBar title="Add / remove tags" userIds={Object.keys(checked)}
          onClear={nChecked ? () => setChecked({}) : undefined}
          onDone={() => list.reload()} />
      )}
      {bulkOpen && (
        <BulkDelete picked={checked} onUnpick={id => unchecked([id])}
          onClearPicked={() => setChecked({})} onDeleted={afterBulk} />
      )}
      {selected && <UserDrawer userId={selected} onClose={() => setSelected(null)}
        onChanged={() => { list.reload(); summary.reload(); }} />}
    </div>
  );
};

/**
 * Bulk delete — targets come from ticked Directory rows, from a pasted email
 * list, or from both at once; the server takes them in one call. Guarded by a
 * typed acknowledgement *and* the two-click DangerBtn, because users/bulk_delete
 * is the same cascading hard delete as the per-row button, ~20 tables deep and
 * with no undo.
 */
const BulkDelete: React.FC<{
  picked: Record<string, string>;
  onUnpick: (id: string) => void;
  onClearPicked: () => void;
  onDeleted: (deletedIds: string[]) => void;
}> = ({ picked, onUnpick, onClearPicked, onDeleted }) => {
  const [raw, setRaw] = useState('');
  const [ack, setAck] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Flash>(null);
  const [results, setResults] = useState<any[] | null>(null);

  const ids = Object.keys(picked);

  const { emails, invalid } = useMemo(() => {
    // Comma / semicolon / newline separated. Addresses already covered by a
    // ticked row are dropped: the server resolves emails to ids and would hit
    // the same account twice, the second pass failing with "User not found".
    const already = new Set(Object.values(picked).map(e => (e || '').toLowerCase()));
    const seen = new Set<string>();
    const ok: string[] = [];
    const bad: string[] = [];
    raw.split(/[\s,;]+/).map(t => t.trim()).filter(Boolean).forEach(t => {
      const e = t.toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { bad.push(t); return; }
      if (already.has(e) || seen.has(e)) return;
      seen.add(e);
      ok.push(e);
    });
    return { emails: ok, invalid: bad };
  }, [raw, picked]);

  const targets = ids.length + emails.length;
  const overCap = targets > BULK_LIMIT;
  const armed = targets > 0 && !overCap && !busy && ack.trim().toUpperCase() === BULK_ACK;

  const run = async () => {
    setBusy(true); setMsg(null);
    const body: Record<string, any> = { confirm: true };
    if (ids.length) body.user_ids = ids;
    if (emails.length) body.emails = emails;
    const { data, error } = await panelPost('users', 'bulk_delete', body);
    setBusy(false);
    if (error || !data?.success) {
      setMsg({ kind: 'error', text: error || 'Bulk delete failed' });
      return;
    }
    const res: any[] = data.results || [];
    setResults(res);
    setMsg({
      kind: data.failed ? 'error' : 'ok',
      text: `Deleted ${data.deleted} user(s)`
        + (data.failed ? `; ${data.failed} failed — see the breakdown below.` : '.'),
    });
    setAck('');
    setRaw('');
    onDeleted(res.filter(r => r.success && r.user_id).map(r => r.user_id));
  };

  return (
    <Section title="Bulk delete"
      right={ids.length > 0 ? <Btn small onClick={onClearPicked}>Clear selection</Btn> : undefined}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Deletes the account and everything hanging off it — chats, searches, saved assets,
        sessions, profile and LinkedIn cache. There is no undo and no email is sent.
        At most {BULK_LIMIT} accounts per run.
      </p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <div className="text-xs font-bold text-grayscale-1200 font-grotesk">
            Selected in the table ({ids.length})
          </div>
          {ids.length ? (
            <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
              {ids.map(id => (
                <span key={id}
                  className="text-[11px] rounded-full border border-grayscale-300 bg-grayscale-200 px-2 py-0.5 flex items-center gap-1">
                  {picked[id] || id}
                  <button onClick={() => onUnpick(id)} title="Remove from selection"
                    className="text-grayscale-1100 hover:text-red-600">×</button>
                </span>
              ))}
            </div>
          ) : (
            <div className="text-xs text-grayscale-1100">
              Tick rows in the Directory above to add them here.
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-xs font-bold text-grayscale-1200 font-grotesk">
            Paste an email list ({emails.length} usable)
          </div>
          <textarea value={raw} rows={5} onChange={e => setRaw(e.target.value)}
            placeholder={'one@example.com\ntwo@example.com'}
            className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 font-mono" />
          {invalid.length > 0 && (
            <div className="text-xs text-amber-700">
              Ignoring {invalid.length} unusable entr{invalid.length === 1 ? 'y' : 'ies'}: {invalid.slice(0, 5).join(', ')}
              {invalid.length > 5 ? '…' : ''}
            </div>
          )}
        </div>
      </div>
      {overCap && (
        <Notice>
          {targets} targets selected — the server refuses more than {BULK_LIMIT} per call. Remove
          {' '}{targets - BULK_LIMIT} and run the rest in a second pass.
        </Notice>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="flex items-end gap-2 flex-wrap">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Type {BULK_ACK} to unlock
          <input value={ack} onChange={e => setAck(e.target.value)} placeholder={BULK_ACK}
            className="h-9 px-3 w-48 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
        </label>
        {armed ? (
          <DangerBtn onConfirm={run}>Delete {targets} user(s)</DangerBtn>
        ) : (
          <Btn kind="danger" disabled
            title={targets === 0 ? 'Select rows or paste emails first'
              : overCap ? `At most ${BULK_LIMIT} per run`
                : busy ? 'Deleting…' : `Type ${BULK_ACK} to unlock`}>
            {busy ? 'Deleting…' : `Delete ${targets} user(s)`}
          </Btn>
        )}
      </div>
      {results && (
        <DataTable rows={results} csvName="bulk_delete_results"
          columns={['email', 'user_id', 'success', 'rows', 'error']}
          labels={{ user_id: 'User id', rows: 'Rows removed', success: 'Result' }}
          empty="Nothing was submitted."
          render={{ success: r => r.success ? <Pill tone="ok">deleted</Pill> : <Pill tone="bad">failed</Pill> }} />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Change sign-in e-mail (owner, 2026-10-05: "this is a crucial step, so add a
// warning … maintain a log for it as well"). Backend: account_email_change.py —
// users/email_change_preview, users/change_email, users/email_changes.
// ---------------------------------------------------------------------------
/** Count keys from the preview (moves / kept) and the change's moved_* keys, as words. */
const COUNT_WORDS: Record<string, [string, string]> = {
  user_admin_status: ['approval record', 'approval records'],
  referred_by: ['referred-by link', 'referred-by links'],
  admin_view_prefs: ['saved admin view', 'saved admin views'],
  promotions: ['targeted offer', 'targeted offers'],
  deletion_requests: ['open deletion request', 'open deletion requests'],
  ip_intel_orders: ['open IP-intel order', 'open IP-intel orders'],
  open_sign_in_codes: ['unused sign-in code (closed)', 'unused sign-in codes (closed)'],
  sign_in_codes_closed: ['unused sign-in code closed', 'unused sign-in codes closed'],
  reset_links_revoked: ['password reset link revoked', 'password reset links revoked'],
  origin_pinned: ['account origin pinned', 'account origin pinned'],
  emails_sent: ['e-mail sent', 'e-mails sent'],
  invites: ['invite', 'invites'],
  delivery_incidents: ['delivery incident', 'delivery incidents'],
};
/** Keys that are a yes/no in effect: "admin rights" reads better than "1 admin rights". */
const FLAG_WORDS: Record<string, string> = { billing_admins: 'admin rights', billing_customers: 'Razorpay customer' };

/** Non-zero counts as short phrases; the account's own users row is implied. */
const countPhrases = (counts: Record<string, any> | null | undefined): string[] =>
  Object.entries(counts || {}).flatMap(([k, v]) => {
    const n = Number(v);
    if (k === 'users' || typeof v === 'boolean' || !Number.isFinite(n) || n <= 0) return [];
    if (FLAG_WORDS[k]) return [FLAG_WORDS[k]];
    const w = COUNT_WORDS[k];
    return [`${n.toLocaleString()} ${w ? (n === 1 ? w[0] : w[1]) : k.replace(/_/g, ' ')}`];
  });

/** What users/change_email reported, as one flash. The change has happened by
 *  the time this runs; a failed after-step (Razorpay, sign-out, our-accounts
 *  list) is shown as an error so it is not missed. */
const describeEmailChange = (d: Record<string, any>): { kind: 'error' | 'ok'; text: string } => {
  const moved: Record<string, any> = {};
  Object.entries(d || {}).forEach(([k, v]) => { if (k.startsWith('moved_')) moved[k.slice(6)] = v; });
  const done = countPhrases(moved);
  const problems: string[] = [];
  const failed = (v: any) => String(v ?? '').toLowerCase().startsWith('failed');
  if (d.sessions_revoked !== undefined && d.sessions_revoked !== null) {
    // ops.revoke_user_sessions returns a dict, which reaches us JSON-encoded.
    let n: any = d.sessions_revoked;
    if (typeof n === 'string' && n.trim().startsWith('{')) {
      try { n = JSON.parse(n)?.revoked_sessions; } catch { /* keep the string */ }
    }
    if (failed(d.sessions_revoked)) problems.push(`Sign-out: ${d.sessions_revoked}`);
    else done.push(`signed out of ${Number(n) || 0} session(s)`);
  }
  if (d.razorpay_customer !== undefined && d.razorpay_customer !== null) {
    if (failed(d.razorpay_customer)) problems.push(`Razorpay customer: ${d.razorpay_customer}`);
    else done.push(`Razorpay: ${d.razorpay_customer}`);
  }
  if (d.exclusion_list !== undefined && d.exclusion_list !== null) {
    if (failed(d.exclusion_list)) problems.push(`Our-accounts list: ${d.exclusion_list}`);
    else done.push(`our-accounts list ${d.exclusion_list}`);
  }
  const head = `E-mail changed: ${d.old_email || '?'} → ${d.new_email || '?'}.`;
  const body = done.length ? ` Moved / done: ${done.join(' · ')}.` : '';
  if (problems.length) {
    return { kind: 'error', text: `${head}${body} Needs attention: ${problems.join(' · ')}` };
  }
  return { kind: 'ok', text: `${head}${body} Logged${d.log_id ? ` as #${d.log_id}` : ''}.` };
};

const ChangeEmailModal: React.FC<{
  userId: string; current: string; onClose: () => void; onDone: (result: Record<string, any>) => void;
}> = ({ userId, current, onClose, onDone }) => {
  const [newEmail, setNewEmail] = useState('');
  const [again, setAgain] = useState('');
  const [reason, setReason] = useState('');
  const [revoke, setRevoke] = useState(false);
  const [updateRz, setUpdateRz] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // `forEmail` is the address the shown preview was checked for, so a stale
  // answer (or one still in flight) never unlocks the button.
  const [pv, setPv] = useState<{ data: any; error: string; loading: boolean; forEmail: string | null }>(
    { data: null, error: '', loading: true, forEmail: null });
  const seq = useRef(0);

  const target = newEmail.trim().toLowerCase();
  const confirmTarget = again.trim().toLowerCase();
  const currentLc = String(current || '').trim().toLowerCase();

  // Once on open with new_email='' (the account's facts), then ~400 ms after
  // typing stops. Newest request wins.
  useEffect(() => {
    const mine = ++seq.current;
    setPv(s => ({ ...s, loading: true }));
    const t = setTimeout(async () => {
      const { data, error } = await panelGet('users', 'email_change_preview',
        { user_id: userId, new_email: target });
      if (mine !== seq.current) return;
      setPv({ data, error, loading: false, forEmail: target });
    }, target ? 400 : 0);
    return () => clearTimeout(t);
  }, [userId, target]);

  const p = pv.data;
  const checkedThis = !pv.loading && pv.forEmail === target && !pv.error;
  const why = !target ? 'Type the new address'
    : target !== confirmTarget ? 'Type the new address again, exactly'
      : target === currentLc ? 'That is already their address'
        : !checkedThis ? (pv.error ? `Could not check the address: ${pv.error}` : 'Checking the address…')
          : p?.ok !== true ? 'The check found a problem — see the red list'
            : '';
  const ready = !why && !busy;

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setErr('');
    const { data, error } = await panelPost('users', 'change_email', {
      user_id: userId, new_email: target, confirm_email: confirmTarget,
      reason: reason.trim().slice(0, 500), confirm: true,
      revoke_sessions: revoke, update_razorpay: updateRz,
    });
    setBusy(false);
    if (error || !data?.success) { setErr(error || data?.error || 'The change failed.'); return; }
    onDone(data);
  };

  const moves = countPhrases(p?.moves);
  const kept = countPhrases(p?.kept);
  const facts = p ? [
    p.name,
    p.is_admin && 'admin',
    p.is_internal && 'one of our own accounts',
    p.clerk_linked && 'signs in with Google / LinkedIn',
    p.has_password && 'has a password',
    (p.subscriptions || []).length && `${p.subscriptions.length} live subscription(s)`,
  ].filter(Boolean) as string[] : [];
  const sessionsN = Number(p?.active_sessions ?? NaN);
  // Not while the second box is still being typed (a prefix of the first).
  const mismatch = !!confirmTarget && !!target && !target.startsWith(confirmTarget);
  const label = target || '…';

  return (
    <Modal title="Change sign-in e-mail" onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          {busy ? <Btn kind="danger" disabled>Changing…</Btn>
            : ready ? (
              <DangerBtn onConfirm={submit}>
                Change to <span className="inline-block max-w-[16rem] truncate align-bottom">{label}</span>
              </DangerBtn>
            ) : (
              <Btn kind="danger" disabled title={why}>
                Change to <span className="inline-block max-w-[16rem] truncate align-bottom">{label}</span>
              </Btn>
            )}
        </>
      }>
      <div className="flex flex-col gap-3 max-h-[65vh] overflow-y-auto pr-1">
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex flex-col gap-1">
          <b className="text-sm">This changes the address they sign in with.</b>
          <ul className="list-disc ml-4 flex flex-col gap-0.5">
            <li>Signing in with the old address (e-mail code, Google or LinkedIn) will no longer reach this account.</li>
            <li>Subscriptions, payments, trials, saved items, searches and tags stay with the account.</li>
            <li>Past e-mail history stays under the old address.</li>
            <li>The change is logged, and can only be undone by another change.</li>
          </ul>
        </div>

        <Labeled label="Current address">
          <div className="h-9 px-3 rounded-md border-[0.5px] border-grayscale-200 bg-grayscale-200 text-sm
            text-grayscale-1300 flex items-center min-w-0"><span className="truncate">{current}</span></div>
        </Labeled>
        {facts.length > 0 && <div className="text-xs text-grayscale-1100 -mt-1">{facts.join(' · ')}</div>}
        <Labeled label="New address">
          <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
            autoComplete="off" spellCheck={false} autoFocus placeholder="name@example.com" className={inputCls} />
        </Labeled>
        <Labeled label="Type the new address again">
          <input type="email" value={again} onChange={e => setAgain(e.target.value)}
            autoComplete="off" spellCheck={false} className={inputCls} />
        </Labeled>
        {mismatch && <div className="text-xs text-red-700 -mt-1">The two addresses do not match.</div>}
        <Labeled label="Reason" hint={`optional · ${reason.length}/500 · kept in the log`}>
          <textarea value={reason} rows={2} maxLength={500} onChange={e => setReason(e.target.value)}
            className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 w-full" />
        </Labeled>

        <div className="flex flex-col gap-1.5">
          <Check checked={revoke} onChange={setRevoke}>
            Sign them out on every device
            {Number.isFinite(sessionsN) ? ` (${sessionsN} active session${sessionsN === 1 ? '' : 's'})` : ''}
          </Check>
          {p?.razorpay_customer && (
            <Check checked={updateRz} onChange={setUpdateRz}>
              Update the e-mail on their Razorpay customer (receipts)
            </Check>
          )}
        </div>

        {pv.loading && <div className="text-xs text-grayscale-1100">Checking…</div>}
        {pv.error && !pv.loading && <Msg kind="error">Could not check the address: {pv.error}</Msg>}
        {p && (
          <>
            {(p.warnings || []).length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <ul className="list-disc ml-4 flex flex-col gap-0.5">
                  {(p.warnings as string[]).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            {(p.blockers || []).length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <b>Cannot change yet:</b>
                <ul className="list-disc ml-4 flex flex-col gap-0.5 mt-0.5">
                  {(p.blockers as string[]).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
            <div className="text-xs text-grayscale-1200 flex flex-col gap-0.5">
              <div><b>What will move:</b> the sign-in address{moves.length ? ` · ${moves.join(' · ')}` : ''}</div>
              {kept.length > 0 && (
                <div><b>Kept as history under the old address:</b> {kept.join(' · ')}</div>
              )}
            </div>
          </>
        )}
        {err && <Msg kind="error">{err}</Msg>}
      </div>
    </Modal>
  );
};

/** An address that opens its delivery trace. */
const TraceLink: React.FC<{ email: any }> = ({ email }) => {
  const e = String(email || '');
  if (!e) return <span className="text-grayscale-1100">—</span>;
  return <AdminLink to={{ group: 'email', tab: 'trace', email: e }} title="Delivery trace for this address">{e}</AdminLink>;
};

// PostHog consolidation (owner, 2026-10-05: "in case there is a email change …
// consolidate it [in PostHog] and keep log for it"). Every change merges the
// old address's PostHog person into the account's current one; the change row
// carries posthog_status / posthog_at / posthog_detail. Backend:
// account_email_change.py — users/email_change_posthog_merge, _check.
type PhTone = 'green' | 'amber' | 'grey' | 'red';
const PH_LOOK: Record<string, { tone: PhTone; label: string; hint: string }> = {
  merged: { tone: 'green', label: 'merged', hint: 'Both addresses are one PostHog person now.' },
  sent: { tone: 'amber', label: 'sent', hint: 'PostHog accepted the merge; it applies within minutes — Check.' },
  no_history: { tone: 'grey', label: 'no history', hint: 'PostHog has nothing under the old address — nothing to merge.' },
  pending: { tone: 'grey', label: 'pending', hint: 'Not merged in PostHog yet — Merge in PostHog.' },
  failed: { tone: 'red', label: 'failed', hint: 'The merge failed.' },
  not_configured: { tone: 'red', label: 'not configured', hint: 'PostHog is not configured on the server.' },
};
const PH_NONE = { tone: 'grey' as PhTone, label: 'not sent', hint: 'No PostHog merge has been sent for this change yet.' };
const phLook = (s: any) => (s ? PH_LOOK[String(s)] : undefined)
  || (s ? { tone: 'grey' as PhTone, label: String(s).replace(/_/g, ' '), hint: '' } : PH_NONE);
const phProblem = (s: any) => s === 'failed' || s === 'not_configured';
/** Which button a change gets: merge (again) until PostHog has it, Check while it applies. */
const phAction = (s: any): 'merge' | 'check' | null =>
  s === 'sent' ? 'check'
    : (!s || s === 'pending' || phProblem(s)) ? 'merge'
      : null;
/** The PostHog state as text — CSV export and the table's own search. */
const phText = (r: Record<string, any>) => {
  const l = phLook(r.posthog_status).label;
  return r.posthog_detail ? `${l} — ${r.posthog_detail}` : l;
};

/** Badge plus one short line: the reason for a failure, or a nudge to Check. */
const PosthogStatus: React.FC<{ row: Record<string, any>; prefix?: boolean }> = ({ row, prefix }) => {
  const s = row.posthog_status;
  const look = phLook(s);
  const title = [row.posthog_detail || look.hint,
    row.posthog_at ? `Last attempt / check ${fmtIST(row.posthog_at)} IST` : ''].filter(Boolean).join('\n');
  return (
    <span className="inline-flex flex-col gap-0.5 min-w-0" title={title}>
      <span className="inline-flex items-center gap-1.5">
        {prefix && <span className="text-[11px] text-grayscale-1100">PostHog</span>}
        <Badge tone={look.tone}>{look.label}</Badge>
      </span>
      {phProblem(s) && row.posthog_detail && (
        <span className="text-[11px] text-red-700 truncate max-w-[20rem]">{row.posthog_detail}</span>
      )}
      {s === 'sent' && <span className="text-[11px] text-grayscale-1100">applies within minutes — Check</span>}
    </span>
  );
};

/** Merge in PostHog / Check, with the server's answer kept per change id (it
 *  is shown next to the row once the list has reloaded) and as `last`. */
function usePosthogActions(onDone: () => void) {
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [said, setSaid] = useState<Record<string, NonNullable<Flash>>>({});
  const [last, setLast] = useState<Flash>(null);
  const run = async (row: Record<string, any>, mode: 'merge' | 'check') => {
    const id = row.id;
    if (id === null || id === undefined) return;
    setBusy(b => ({ ...b, [id]: true }));
    const { data, error } = await panelPost('users',
      mode === 'merge' ? 'email_change_posthog_merge' : 'email_change_posthog_check', { id });
    setBusy(b => { const n = { ...b }; delete n[id]; return n; });
    const st = data?.posthog_status;
    const head = mode === 'merge' ? 'Merge in PostHog' : 'PostHog check';
    const text = error ? `${head} failed: ${error}`
      : `${head}: ${phLook(st).label}${data?.posthog_detail ? ` — ${data.posthog_detail}` : ''}`;
    const kind: 'error' | 'ok' = error || data?.success === false || phProblem(st) ? 'error' : 'ok';
    setSaid(s => ({ ...s, [id]: { kind, text } }));
    setLast({ kind, text: `${row.old_email || '?'} → ${row.new_email || '?'} · ${text}` });
    onDone();
  };
  return { busy, said, last, setLast, run };
}
type PosthogActions = ReturnType<typeof usePosthogActions>;

const PosthogBtn: React.FC<{ row: Record<string, any>; ph: PosthogActions }> = ({ row, ph }) => {
  const mode = phAction(row.posthog_status);
  if (!mode || row.id === null || row.id === undefined) return null;
  const busy = !!ph.busy[row.id];
  return mode === 'merge'
    ? (
      <Btn small disabled={busy} onClick={() => ph.run(row, 'merge')}
        title="Merge the old address's PostHog person into the current address (safe to repeat)">
        {busy ? 'Merging…' : 'Merge in PostHog'}
      </Btn>
    ) : (
      <Btn small disabled={busy} onClick={() => ph.run(row, 'check')}
        title="Check whether PostHog has applied the merge (reads only)">
        {busy ? 'Checking…' : 'Check'}
      </Btn>
    );
};

/** One account's e-mail change log; hidden when there is none. Each address
 *  opens its delivery trace. Until released, a retired address cannot sign up
 *  again (that would be a second, empty account); Release lets it — for a typo
 *  that was fixed, or a mailbox that is now someone else's. */
const EmailChanges: React.FC<{ rows: any[]; onChanged: () => void }> = ({ rows, onChanged }) => {
  const [err, setErr] = useState('');
  const ph = usePosthogActions(onChanged);
  if (!rows.length) return null;
  const release = async (id: number) => {
    setErr('');
    const { error } = await panelPost('users', 'email_change_release', { id });
    if (error) setErr(error); else onChanged();
  };
  return (
    <Section title={`E-mail changes (${rows.length})`}>
      <div className="flex flex-col divide-y divide-grayscale-200">
        {rows.map((r, i) => {
          const said = r.id !== null && r.id !== undefined ? ph.said[r.id] : undefined;
          return (
            <div key={r.id ?? i} className="py-1.5 flex flex-col gap-0.5 text-sm">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                <TraceLink email={r.old_email} />
                <span className="text-grayscale-1100">→</span>
                <TraceLink email={r.new_email} />
              </div>
              <div className="text-[11px] text-grayscale-1100 break-words">
                {fmtIST(r.created_at)} IST · by {r.changed_by || 'unknown'}
                {r.reason ? <> · <span className="text-grayscale-1200">{r.reason}</span></> : ''}
              </div>
              <div className="text-[11px] text-grayscale-1100 flex items-center gap-2 flex-wrap">
                {r.released_at
                  ? <span>Old address released {fmtIST(r.released_at)} IST{r.released_by ? ` by ${r.released_by}` : ''} — it can be used to sign up again.</span>
                  : <>
                      <span>Old address is retired: signing in or signing up with it is refused.</span>
                      <DangerBtn small onConfirm={() => release(r.id)}>Release old address</DangerBtn>
                    </>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <PosthogStatus row={r} prefix />
                <PosthogBtn row={r} ph={ph} />
              </div>
              {said && (
                <div className={`text-[11px] break-words ${said.kind === 'error' ? 'text-red-700' : 'text-green-700'}`}>
                  {said.text}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {err && <Msg kind="error">{err}</Msg>}
    </Section>
  );
};

/** posthog_at / created_at as "YYYY-MM-DD HH:mm" in IST: sorts as text and
 *  exports unambiguously (fmtCell would re-render a date-named column in the
 *  browser's own zone). */
const istStamp = (v: any): string => {
  if (v === null || v === undefined || v === '') return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return new Date(d.getTime() + 330 * 60000).toISOString().slice(0, 16).replace('T', ' ');
};

/**
 * Users → E-mail changes: every sign-in address change across accounts, old
 * and current address, who changed it and why, whether the old address is
 * still retired, and whether PostHog has folded the two into one person.
 * Backend: users/email_changes_all (account_email_change.all_changes).
 */
const EmailChangeLog: React.FC = () => {
  const [qRaw, setQRaw] = useState('');
  const q = useDebounced(qRaw.trim());
  const log = useAdminGet('users', 'email_changes_all', { q, limit: 200 }, [q]);
  const [selected, setSelected] = useState<string | null>(null);
  const [msg, setMsg] = useState<Flash>(null);
  const ph = usePosthogActions(() => log.reload());

  const rows = useMemo(() => (log.data?.rows || []).map((r: any) => ({
    ...r,
    time_ist: istStamp(r.created_at),
    account: r.user ? [r.user.name, r.user.email].filter(Boolean).join(' · ') : 'account deleted',
    address_change: `${r.old_email || ''} → ${r.new_email || ''}`,
    posthog: phText(r),
    old_address: r.released_at
      ? `released ${istStamp(r.released_at)} IST${r.released_by ? ` by ${r.released_by}` : ''}`
      : 'retired',
  })), [log.data]);
  const total = Number(log.data?.total ?? rows.length) || 0;
  const configured = log.data?.posthog_configured;
  const note = String(log.data?.posthog_note || '');
  const flash = ph.last || msg;

  const release = async (r: Record<string, any>) => {
    ph.setLast(null);
    const { data, error } = await panelPost('users', 'email_change_release', { id: r.id });
    if (error) setMsg({ kind: 'error', text: error });
    else {
      setMsg({ kind: 'ok', text: `Released ${data?.released_email || r.old_email} — it can be used to sign up again.` });
      log.reload();
    }
  };
  const lc = (v: any) => String(v || '').trim().toLowerCase();

  return (
    <div className="flex flex-col gap-4">
      <Section title={`E-mail changes (${total.toLocaleString()})`}
        right={<Btn small onClick={log.reload}>Refresh</Btn>}>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={qRaw} onChange={setQRaw}
            placeholder="Search old / new address, current address, changed by, reason…" wide />
          {rows.length < total && (
            <span className="text-xs text-grayscale-1100">newest {rows.length} of {total.toLocaleString()}</span>
          )}
        </div>
        {configured === false
          ? <Msg kind="error">PostHog is not configured on the server, so an old and a current address stay two
              people in product analytics until it is.{note ? ` (${note})` : ''}</Msg>
          : note && <div className="text-xs text-grayscale-1100">{note}</div>}
        {flash && <Msg kind={flash.kind}>{flash.text}</Msg>}
        {log.error && <Msg kind="error">{log.error}</Msg>}
        {log.loading && !log.data ? <Loading /> : (
          <DataTable rows={rows} csvName="email_changes"
            columns={['time_ist', 'account', 'address_change', 'changed_by', 'reason', 'posthog', 'old_address']}
            labels={{
              time_ist: 'When (IST)', account: 'Account (now)', address_change: 'Old address → Current address',
              changed_by: 'Changed by', posthog: 'PostHog', old_address: 'Old address',
            }}
            empty={q ? 'No e-mail change matches that search.' : 'No sign-in e-mail has been changed yet.'}
            render={{
              time_ist: r => <span title={r.created_at}>{fmtIST(r.created_at)}</span>,
              account: r => r.user?.user_id
                ? (
                  <button type="button" onClick={() => setSelected(String(r.user.user_id))}
                    title="Open this account" className="text-left text-primary-700 hover:underline">
                    {r.user.name && <span className="font-medium">{r.user.name} · </span>}
                    {r.user.email}
                  </button>
                ) : <span className="text-grayscale-1100">account deleted</span>,
              address_change: r => (
                <span className="inline-flex items-center gap-1.5">
                  <TraceLink email={r.old_email} />
                  <span className="text-grayscale-1100">→</span>
                  <TraceLink email={r.new_email} />
                  {r.user?.email && lc(r.user.email) !== lc(r.new_email) && (
                    <span className="text-[11px] text-grayscale-1100"
                      title={`Changed again later — the account is ${r.user.email} now`}>(changed again)</span>
                  )}
                </span>
              ),
              reason: r => r.reason
                ? <span title={r.reason}>{r.reason}</span>
                : <span className="text-grayscale-1100">—</span>,
              posthog: r => <PosthogStatus row={r} />,
              old_address: r => r.released_at
                ? (
                  <span className="text-[11px] text-grayscale-1200"
                    title="Released — the old address can be used to sign up again">
                    released {fmtIST(r.released_at)}{r.released_by ? ` · ${r.released_by}` : ''}
                  </span>
                ) : (
                  <span title="Signing in or signing up with the old address is refused">
                    <Badge tone="grey">retired</Badge>
                  </span>
                ),
            }}
            actions={r => (
              <div className="flex gap-1 items-center">
                <PosthogBtn row={r} ph={ph} />
                {!r.released_at && r.id !== null && r.id !== undefined && (
                  <DangerBtn small onConfirm={() => release(r)}>Release old address</DangerBtn>
                )}
              </div>
            )} />
        )}
      </Section>
      {selected && <UserDrawer userId={selected}
        onClose={() => { setSelected(null); log.reload(); }}
        onChanged={() => log.reload()} />}
    </div>
  );
};

/** Detail + profile editor + timeline + sessions for one user. */
const UserDrawer: React.FC<{ userId: string; onClose: () => void; onChanged: () => void }> =
  ({ userId, onClose, onChanged }) => {
  const detail = useAdminGet('users', 'detail', { user_id: userId }, [userId]);
  const timeline = useAdminGet('users', 'timeline', { user_id: userId }, [userId]);
  const sessions = useAdminGet('users', 'sessions', { user_id: userId }, [userId]);
  const emailChanges = useAdminGet('users', 'email_changes', { user_id: userId }, [userId]);
  const [edit, setEdit] = useState<Record<string, any>>({});
  const [msg, setMsg] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  // Bumped when the plan changes, so "University contacts" re-reads the
  // account's tier and limit.
  const [contactsKey, setContactsKey] = useState(0);
  const seeded = useRef('');
  // A deep link can swap the account under an open drawer; never carry the
  // e-mail dialog over to a different account.
  useEffect(() => { setEmailOpen(false); }, [userId]);

  const u = detail.data?.user || {};
  const activeSessions = sessions.data?.count ?? detail.data?.session_info?.active_sessions ?? 0;

  useEffect(() => {
    // Seed the editor once per user. A status toggle refetches the detail, and
    // re-seeding on every payload would silently throw away profile edits that
    // were typed but not saved yet.
    if (!detail.data || seeded.current === userId) return;
    seeded.current = userId;
    const du = detail.data.user || {};
    const dp = detail.data.profile || {};
    setEdit({
      first_name: du.first_name || '', last_name: du.last_name || '',
      country: du.country || '', city: du.city || '',
      organisation_name: dp.organisation_name || '', designation: dp.designation || '',
      linkedin_profile_url: dp.linkedin_profile_url || '',
      professional_summary: dp.professional_summary || '',
    });
  }, [detail.data, userId]);

  const save = async () => {
    setBusy(true);
    const { data, error } = await panelPost('users', 'update', { user_id: userId, updates: edit });
    setBusy(false);
    if (error || !data?.success) setMsg({ kind: 'error', text: error || 'Save failed' });
    else { setMsg({ kind: 'ok', text: `Saved (${(data.updated_fields || []).length} fields).` }); onChanged(); }
  };

  const setStatus = async (field: string, next: boolean) => {
    setStatusBusy(field);
    const { data, error } = await panelPost('users', 'status',
      { user_id: userId, updates: { [field]: next } });
    setStatusBusy('');
    if (error || !data?.success) {
      setMsg({ kind: 'error', text: error || 'Status change failed' });
      return;
    }
    // The handler echoes the fields it actually wrote. Anything it ignored has
    // to surface here — a status that reports success but never lands is the
    // exact failure this panel shipped with.
    if (!(data.updated || []).includes(field)) {
      setMsg({ kind: 'error', text: `Server did not apply ${field.replace('is_', '')}.` });
      return;
    }
    setMsg({ kind: 'ok', text: `${field.replace('is_', '')} → ${next ? 'yes' : 'no'}.` });
    await detail.reload();
    onChanged();
  };

  const regenerate = async () => {
    setMsg(null);
    const { data, error } = await panelPost('users', 'linkedin_regenerate',
      { user_id: userId, linkedin_url: edit.linkedin_profile_url || '' });
    if (error) setMsg({ kind: 'error', text: error });
    else setMsg({ kind: 'ok', text: data?.message || 'Queued.' });
  };

  const revoke = async () => {
    const { data, error } = await panelPost('users', 'revoke_sessions', { user_id: userId });
    if (error) setMsg({ kind: 'error', text: error });
    else {
      // db_ops.revoke_user_sessions reports the count as revoked_sessions.
      setMsg({ kind: 'ok', text: `Revoked ${data?.revoked_sessions ?? 0} session(s).` });
      detail.reload();
      sessions.reload();
    }
  };

  const emailChanged = (result: Record<string, any>) => {
    setEmailOpen(false);
    setMsg(describeEmailChange(result));
    detail.reload();
    sessions.reload();
    timeline.reload();
    emailChanges.reload();
    onChanged();
  };

  // Suspending signs them out everywhere; reinstating does not sign them back in.
  const accessChanged = () => {
    detail.reload();
    sessions.reload();
    timeline.reload();
    onChanged();
  };

  const planChanged = () => {
    detail.reload();
    timeline.reload();
    setContactsKey(k => k + 1);
    onChanged();
  };

  const field = (k: string, label: string, wide = false) => (
    <label className={`flex flex-col gap-1 text-xs text-grayscale-1100 ${wide ? 'col-span-2' : ''}`}>
      {label}
      <input value={edit[k] ?? ''} onChange={e => setEdit(s => ({ ...s, [k]: e.target.value }))}
        className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-2xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk break-all">{u.email || '…'}</h2>
              {u.is_active === false && (
                <span title="This account cannot sign in. See Account access below.">
                  <Pill tone="bad">suspended</Pill>
                </span>
              )}
              {u.email && (
                <Btn small kind="danger" onClick={() => setEmailOpen(true)}
                  title="Change the address this account signs in with (logged)">
                  Change e-mail…
                </Btn>
              )}
            </div>
            <div className="text-xs text-grayscale-1100">
              Joined {fmtDate(u.created_at)} · {activeSessions} active session(s)
            </div>
          </div>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {emailOpen && u.email && (
          <ChangeEmailModal userId={userId} current={String(u.email)}
            onClose={() => setEmailOpen(false)} onDone={emailChanged} />
        )}
        {/* Only the first load blanks the drawer: a reload after a suspend or plan
            change must not unmount the sections and wipe what they just reported. */}
        {detail.loading && !detail.data ? <Loading /> : (
          <>
            <AccountAccessSection userId={userId} email={u.email ? String(u.email) : undefined}
              onChanged={accessChanged} />
            <Section title="Status">
              <div className="flex gap-2 flex-wrap text-sm items-center">
                {DRAWER_TOGGLES.map(k => (
                  <Btn key={k} small kind={u[k] ? 'primary' : 'subtle'}
                    disabled={!!statusBusy}
                    title={`Set ${k.replace('is_', '')} to ${u[k] ? 'no' : 'yes'}`}
                    onClick={() => setStatus(k, !u[k])}>
                    {k.replace('is_', '')}: {statusBusy === k ? '…' : (u[k] ? 'yes' : 'no')}
                  </Btn>
                ))}
                <span className="text-[11px] text-grayscale-1100">
                  To stop someone signing in, use Suspend under Account access.
                </span>
              </div>
            </Section>
            <PlanSection userId={userId} onChanged={planChanged} />
            <ContactLimitSection userId={userId} onChanged={onChanged} reloadKey={contactsKey} />
            <EmailChanges rows={emailChanges.data?.rows || []} onChanged={() => emailChanges.reload()} />
            <Section title={`Sessions (${activeSessions} active)`}
              right={<Btn small onClick={revoke} disabled={!activeSessions}>Revoke sessions</Btn>}>
              {sessions.error && <Msg kind="error">{sessions.error}</Msg>}
              {sessions.loading ? <Loading /> : (
                <DataTable rows={sessions.data?.sessions || []}
                  columns={['created_at', 'ip', 'user_agent', 'access_expires_at']}
                  labels={{ access_expires_at: 'Access expires', ip: 'IP' }}
                  maxHeight="12rem" empty="No active sessions." />
              )}
            </Section>
            <Section title="Profile" right={<Btn small kind="primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save profile'}</Btn>}>
              <div className="grid grid-cols-2 gap-3">
                {field('first_name', 'First name')}
                {field('last_name', 'Last name')}
                {field('organisation_name', 'Organisation')}
                {field('designation', 'Designation')}
                {field('country', 'Country')}
                {field('city', 'City')}
                {field('linkedin_profile_url', 'LinkedIn URL', true)}
              </div>
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
                Professional summary
                <textarea value={edit.professional_summary ?? ''} rows={4}
                  onChange={e => setEdit(s => ({ ...s, professional_summary: e.target.value }))}
                  className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <Btn small disabled={!edit.linkedin_profile_url} onClick={regenerate}
                  title={edit.linkedin_profile_url
                    ? 'Re-scrape the LinkedIn profile and rewrite this summary'
                    : 'Add a LinkedIn URL first'}>
                  Regenerate from LinkedIn
                </Btn>
                <span className="text-[11px] text-grayscale-1100">
                  Runs as a background task — it does not overwrite anything until it succeeds.
                </span>
              </div>
            </Section>
            <Section title="Activity timeline">
              {timeline.loading ? <Loading /> : (
                <div className="flex flex-col gap-1 max-h-72 overflow-y-auto text-sm">
                  {(timeline.data || []).map((e: any, i: number) => (
                    <div key={i} className="flex gap-2 items-baseline">
                      <span className="text-xs text-grayscale-1100 whitespace-nowrap">{fmtDate(e.when)}</span>
                      <span className="text-xs font-bold text-primary-700 uppercase">{e.label || e.kind}</span>
                      <span className="text-grayscale-1300 truncate" title={e.detail}>{e.detail}</span>
                    </div>
                  ))}
                  {!(timeline.data || []).length && <span className="text-grayscale-1100 text-sm">No recorded activity.</span>}
                </div>
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Approvals — user_admin_status rows awaiting a decision. "Approve & Create
// Account" enqueues a durable background task (see the Task queue tab); it is
// not the same as flipping is_approved on an existing user.
// ---------------------------------------------------------------------------
const Approvals: React.FC<{ onOpenTasks: () => void }> = ({ onOpenTasks }) => {
  const [includeProcessing, setIncludeProcessing] = useState(true);
  const [open, setOpen] = useState<any>(null);
  const [msg, setMsg] = useState<Flash>(null);
  const [lastTask, setLastTask] = useState('');

  const cfg = useAdminGet('users', 'task_config');
  const approvals = useAdminGet('users', 'pending_approvals',
    { limit: 200, include_processing: includeProcessing ? 'true' : 'false' },
    [includeProcessing]);

  const rows: any[] = approvals.data?.rows || [];
  const orphaned = approvals.data?.orphaned || 0;

  const reject = async (row: any) => {
    const { data, error } = await panelPost('users', 'reject_request', { request_id: row.id });
    if (error) setMsg({ kind: 'error', text: error });
    else {
      setMsg({ kind: 'ok', text: data?.message || `Rejected ${row.email}.` });
      setOpen(null);
      approvals.reload();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Pending" value={approvals.data?.pending ?? '—'} />
        <KPI label="Processing" value={approvals.data?.processing ?? '—'} />
        <KPI label="Orphaned" value={orphaned} hint={orphaned ? 'stuck, safe to re-approve' : undefined} />
        <KPI label="Shown" value={rows.length} />
      </KPIRow>
      <ConfigNotices cfg={cfg.data} />
      {orphaned > 0 && (
        <Notice>
          {orphaned} request(s) sit in <b>Processing</b> with no live task behind them — the old
          Streamlit queue died with its session. They are safe to approve again; approving starts a
          fresh durable task.
        </Notice>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {lastTask && (
        <div className="flex items-center gap-2 text-xs text-grayscale-1100">
          Task {lastTask} is running in the background.
          <Btn small onClick={onOpenTasks}>Open task queue</Btn>
        </div>
      )}
      {approvals.error && <Msg kind="error">{approvals.error}</Msg>}
      <Section title="Pending approvals"
        right={
          <div className="flex items-center gap-3">
            <Check checked={includeProcessing} onChange={setIncludeProcessing}>
              Include Processing
            </Check>
            <Btn small onClick={approvals.reload}>Refresh</Btn>
          </div>
        }>
        {approvals.loading ? <Loading /> : (
          <DataTable rows={rows} searchable csvName="pending_approvals"
            columns={['email', 'display_name', 'organisation', 'designation', 'country',
              'linkedin_url', 'invite_status', 'task_status', 'account_exists', 'orphaned',
              'referred_by_label', 'created_at']}
            labels={{
              display_name: 'Name', referred_by_label: 'Referred by', account_exists: 'Account',
              task_status: 'Latest task', linkedin_url: 'LinkedIn',
            }}
            empty="No requests waiting for a decision."
            render={{
              linkedin_url: r => LinkCell(r),
              task_status: r => taskPill(r.task_status),
              account_exists: r => r.account_exists
                ? <Pill tone="ok">exists</Pill> : <span className="text-grayscale-1100">—</span>,
              orphaned: r => r.orphaned ? <Pill tone="warn">orphaned</Pill> : <span className="text-grayscale-1100">—</span>,
            }}
            actions={r => (
              <div className="flex gap-1">
                <Btn small onClick={() => setOpen(r)}>{r.can_approve ? 'Review & approve' : 'Open'}</Btn>
                <DangerBtn small onConfirm={() => reject(r)}>Reject</DangerBtn>
              </div>
            )} />
        )}
      </Section>
      {open && (
        <ApprovalDrawer row={open} cfg={cfg.data} onClose={() => setOpen(null)}
          onReject={() => reject(open)}
          onApproved={(taskId, email) => {
            setLastTask(taskId);
            setMsg({ kind: 'ok', text: `Account creation queued for ${email} (task ${taskId}).` });
            setOpen(null);
            approvals.reload();
          }} />
      )}
    </div>
  );
};

const ApprovalDrawer: React.FC<{
  row: any; cfg: any; onClose: () => void; onReject: () => void;
  onApproved: (taskId: string, email: string) => void;
}> = ({ row, cfg, onClose, onReject, onApproved }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const approve = async () => {
    setBusy(true); setErr('');
    const { data, error } = await panelPost('users', 'approve_request',
      { request_id: row.id, confirm: true });
    setBusy(false);
    if (error || !data?.success) setErr(error || 'Approval failed');
    else onApproved(data.task_id, data.email || row.email);
  };

  const line = (label: string, value: React.ReactNode) => (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-grayscale-1100">{label}</span>
      <span className="text-grayscale-1300 text-right break-words">{value ?? '—'}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">{row.display_name || row.email}</h2>
            <div className="text-xs text-grayscale-1100">
              {row.email} · requested {fmtDate(row.created_at)}
            </div>
          </div>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        {err && <Msg kind="error">{err}</Msg>}
        <Section title="Request">
          {line('Invite status', <Pill tone={row.orphaned ? 'warn' : 'mute'}>{row.invite_status || '—'}</Pill>)}
          {line('User status', row.user_status)}
          {line('Latest task', row.task_id ? <span className="flex gap-2 items-center justify-end">{taskPill(row.task_status)}<code className="text-xs">{row.task_id}</code></span> : '—')}
          {row.task_error && <Msg kind="error">{row.task_error}</Msg>}
          {line('Account already exists', row.account_exists ? <Pill tone="ok">yes</Pill> : 'no')}
          {line('Referred by', row.referred_by_label || row.referred_by_email)}
          {line('Last updated', fmtDate(row.updated_at))}
        </Section>
        <Section title="Profile that will be created">
          {line('First name', row.first_name)}
          {line('Last name', row.last_name)}
          {line('Organisation', row.organisation)}
          {line('Designation', row.designation)}
          {line('Country', row.country)}
          {line('LinkedIn', LinkCell(row))}
        </Section>
        <Section title="Decision">
          <ConfigNotices cfg={cfg} />
          {row.orphaned && (
            <Notice>
              This row is pinned to <b>Processing</b> with no live task. Approving restarts it from
              scratch — check the account does not already exist first.
            </Notice>
          )}
          <p className="text-xs text-grayscale-1100">
            Approving enqueues a background task that creates the account, optionally enriches it
            from LinkedIn and sends the welcome email. It cannot be recalled once the mail goes out.
          </p>
          <Check checked={confirm} onChange={setConfirm}>
            I confirm this person should get an account and the welcome email.
          </Check>
          <div className="flex gap-2 flex-wrap">
            <Btn kind="primary" disabled={!confirm || busy || !row.can_approve} onClick={approve}
              title={row.can_approve ? undefined : 'A task is already in flight for this request'}>
              {busy ? 'Queueing…' : 'Approve & create account'}
            </Btn>
            <DangerBtn onConfirm={onReject}>Reject request</DangerBtn>
          </div>
          {!row.can_approve && (
            <p className="text-xs text-grayscale-1100">
              Approval is blocked while a task is in flight. Watch it in the Task queue tab.
            </p>
          )}
        </Section>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Task queue — the durable worker behind approvals and bulk uploads.
// ---------------------------------------------------------------------------
const TASK_STATUSES = ['All', 'Pending', 'Processing', 'Completed', 'Failed'];
const TASK_TYPES = ['All', 'Approval', 'Bulk Upload'];

const TaskQueue: React.FC = () => {
  const [status, setStatus] = useState('All');
  const [type, setType] = useState('All');
  const [emailRaw, setEmailRaw] = useState('');
  const [taskIdRaw, setTaskIdRaw] = useState('');
  const [auto, setAuto] = useState(false);
  const [allowRetry, setAllowRetry] = useState(false);
  const [tick, setTick] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState<Flash>(null);

  const email = useDebounced(emailRaw);
  const taskId = useDebounced(taskIdRaw);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => setTick(x => x + 1), 5000);
    return () => clearInterval(t);
  }, [auto]);

  const cfg = useAdminGet('users', 'task_config');
  const params = useMemo(() => {
    const p: Record<string, string | number> = { status, task_type: type, limit: 200 };
    if (email.trim()) p.email = email.trim();
    if (taskId.trim()) p.task_id = taskId.trim();
    return p;
  }, [status, type, email, taskId]);
  // The GET is deliberately not read-only: it sweeps abandoned rows and restarts
  // tasks a server restart left pending, so refreshing is also the recovery path.
  const tasks = useAdminGet('users', 'tasks', params, [status, type, email, taskId, tick]);

  const rows: any[] = tasks.data?.rows || [];
  const stats = tasks.data?.stats || {};
  const clearable = (stats.completed || 0) + (stats.failed || 0);
  const detail = rows.find(r => r.task_id === open);

  const act = async (action: string, body: Record<string, any>, okText: string) => {
    const { data, error } = await panelPost('users', action, body);
    if (error || data?.success === false) setMsg({ kind: 'error', text: error || 'Failed' });
    else { setMsg({ kind: 'ok', text: okText }); tasks.reload(); }
    return !error;
  };

  const retry = (r: any) => act('task_retry', { task_id: r.task_id, confirm: true },
    `Task ${r.task_id} re-queued for ${r.email}.`);
  const remove = (r: any) => act('task_delete', { task_id: r.task_id },
    `Task ${r.task_id} removed from the queue.`);

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Total" value={stats.total ?? '—'} />
        <KPI label="Pending" value={stats.pending ?? '—'} />
        <KPI label="Processing" value={stats.processing ?? '—'} hint={cfg.data ? `${cfg.data.worker_slots} worker slot(s)` : undefined} />
        <KPI label="Completed" value={stats.completed ?? '—'} />
        <KPI label="Failed" value={stats.failed ?? '—'} />
      </KPIRow>
      <ConfigNotices cfg={cfg.data} />
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {tasks.error && <Msg kind="error">{tasks.error}</Msg>}
      <Section title="Queue"
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <Check checked={auto} onChange={setAuto}>Auto-refresh (5s)</Check>
            <Btn small onClick={tasks.reload}>Refresh</Btn>
            <DangerBtn small onConfirm={() => act('tasks_clear_completed', {},
              'Completed and failed tasks cleared.')}>
              Clear completed ({clearable})
            </DangerBtn>
          </div>
        }>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={status} onChange={setStatus} options={TASK_STATUSES}
            labels={{ All: 'All statuses' }} />
          <Select value={type} onChange={setType} options={TASK_TYPES} labels={{ All: 'All types' }} />
          <TextInput value={emailRaw} onChange={setEmailRaw} placeholder="Email contains…" />
          <TextInput value={taskIdRaw} onChange={setTaskIdRaw} placeholder="Exact task id…" />
          <span className="text-xs text-grayscale-1100">
            Showing {tasks.data?.shown ?? rows.length} of {stats.total ?? 0}
            {cfg.data ? ` · up to ${cfg.data.max_linkedin_attempts} LinkedIn attempt(s) per task` : ''}
          </span>
        </div>
        <Check checked={allowRetry} onChange={setAllowRetry}>
          Enable retry — re-running a finished task creates the account again and re-sends the
          welcome email.
        </Check>
        {tasks.loading && !rows.length ? <Loading /> : (
          <DataTable rows={rows} csvName="user_creation_tasks"
            columns={['task_id', 'email', 'type_label', 'status', 'linkedin_attempts_label',
              'retry_count', 'duration_seconds', 'created_at', 'completed_at', 'error_message']}
            labels={{
              task_id: 'Task', type_label: 'Type', linkedin_attempts_label: 'LinkedIn',
              retry_count: 'Retries', duration_seconds: 'Duration', error_message: 'Error',
            }}
            empty="No tasks match these filters."
            render={{
              status: r => (
                <span className="flex gap-1 items-center">
                  {taskPill(r.status)}
                  {r.partial_success && <Pill tone="warn">partial</Pill>}
                </span>
              ),
              duration_seconds: r => fmtSecs(r.duration_seconds),
              retry_count: r => `${r.retry_count ?? 0}/${r.max_retries ?? 0}`,
            }}
            actions={r => (
              <div className="flex gap-1">
                <Btn small onClick={() => setOpen(r.task_id)}>Open</Btn>
                {allowRetry && (r.status === 'completed' || r.status === 'failed') && (
                  <DangerBtn small onConfirm={() => retry(r)}>Retry</DangerBtn>
                )}
                {r.removable && <DangerBtn small onConfirm={() => remove(r)}>Remove</DangerBtn>}
              </div>
            )} />
        )}
      </Section>
      {detail && (
        <TaskDrawer row={detail} allowRetry={allowRetry} onClose={() => setOpen(null)}
          onRetry={() => retry(detail)}
          onRemove={() => { remove(detail); setOpen(null); }} />
      )}
    </div>
  );
};

const TaskDrawer: React.FC<{
  row: any; allowRetry: boolean; onClose: () => void; onRetry: () => void; onRemove: () => void;
}> = ({ row, allowRetry, onClose, onRetry, onRemove }) => {
  const line = (label: string, value: React.ReactNode) => (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-grayscale-1100">{label}</span>
      <span className="text-grayscale-1300 text-right break-words">{value ?? '—'}</span>
    </div>
  );
  const finished = row.status === 'completed' || row.status === 'failed';

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">{row.email}</h2>
            <div className="text-xs text-grayscale-1100">
              <code>{row.task_id}</code> · {row.type_label || row.task_type}
            </div>
          </div>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        {row.partial_success && (
          <Notice>
            <b>Partial success.</b> The account was created but a later step (LinkedIn enrichment or
            the welcome email) did not complete — see the error below.
          </Notice>
        )}
        {row.error_message && <Msg kind="error">{row.error_message}</Msg>}
        <Section title="Progress">
          {line('Status', <span className="flex gap-1 justify-end">{taskPill(row.status)}{row.running && <Pill tone="warn">worker live</Pill>}</span>)}
          {line('Created', fmtDate(row.created_at))}
          {line('Started', fmtDate(row.started_at))}
          {line('Completed', fmtDate(row.completed_at))}
          {line('Duration', fmtSecs(row.duration_seconds))}
          {line('Attempts', `${row.retry_count ?? 0} retry / ${row.max_retries ?? 0} max`)}
          {line('LinkedIn attempts', row.linkedin_attempts_label || row.linkedin_attempts)}
          {line('Queued by', row.created_by)}
          {line('Approval row', row.admin_status_id)}
        </Section>
        <Section title="Account being created">
          {line('First name', row.first_name)}
          {line('Last name', row.last_name)}
          {line('Organisation', row.organisation)}
          {line('Designation', row.designation)}
          {line('Country', row.country)}
          {line('LinkedIn', LinkCell(row))}
          {line('Referred by', row.referred_by_label || row.referred_by_email)}
          {line('Auto verify / approve', `${row.auto_verify ? 'yes' : 'no'} / ${row.auto_approve ? 'yes' : 'no'}`)}
        </Section>
        <Section title="Queue actions">
          <p className="text-xs text-grayscale-1100">
            Retry resets the task and starts a fresh worker — the account creation and the welcome
            email run again. Remove only deletes the queue row; it never touches the account, and is
            refused while the task is pending or processing.
          </p>
          <div className="flex gap-2 flex-wrap">
            {finished && allowRetry && <DangerBtn onConfirm={onRetry}>Retry this task</DangerBtn>}
            {row.removable && <DangerBtn onConfirm={onRemove}>Remove from queue</DangerBtn>}
          </div>
          {finished && !allowRetry && (
            <p className="text-xs text-grayscale-1100">
              Retry is locked. Tick <b>Enable retry</b> in the queue toolbar to unlock it.
            </p>
          )}
          {!finished && (
            <p className="text-xs text-grayscale-1100">
              A pending or processing task can neither be retried nor removed — wait for the worker
              to finish.
            </p>
          )}
        </Section>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const AddUser: React.FC = () => {
  const empty = { email: '', first_name: '', last_name: '', country: '', organisation: '', designation: '', linkedin_url: '' };
  const [f, setF] = useState<Record<string, string>>(empty);
  const [msg, setMsg] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost('users', 'create', f);
    setBusy(false);
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `User created (${data.user_id}).` }); setF(empty); }
  };

  const field = (k: string, label: string, type = 'text') => (
    <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
      {label}
      <input type={type} value={f[k]} onChange={e => setF(s => ({ ...s, [k]: e.target.value }))}
        className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
    </label>
  );

  return (
    <Section title="Add an individual user">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl">
        {field('email', 'Email *', 'email')}
        {field('first_name', 'First name')}
        {field('last_name', 'Last name')}
        {field('country', 'Country')}
        {field('organisation', 'Organisation')}
        {field('designation', 'Designation')}
      </div>
      <div className="max-w-3xl">{field('linkedin_url', 'LinkedIn URL')}</div>
      <div>
        <Btn kind="primary" disabled={busy || !f.email} onClick={submit}>
          {busy ? 'Creating…' : 'Create user'}
        </Btn>
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
const BULK_COLS = ['email', 'first_name', 'last_name', 'country', 'organisation', 'designation', 'linkedin_url'];

const BulkAdd: React.FC = () => {
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [msg, setMsg] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  // Off by default: adding accounts and mailing them are separate decisions,
  // and a bulk import that silently mails hundreds of people cannot be undone.
  const [sendEmail, setSendEmail] = useState(false);
  const [tpl, setTpl] = useState<any>(null);
  const [tplOpen, setTplOpen] = useState(false);
  // The run the invites went out under (bulk_create → run_id), for the
  // "track them in Email Center" link under the result.
  const [tracked, setTracked] = useState<{ run_id: string; emailed: number } | null>(null);

  // Fetched the moment the box is ticked, so the copy can be read BEFORE
  // anyone presses send. Comes from the same store the send resolves, so the
  // preview cannot show one thing and the email deliver another.
  useEffect(() => {
    if (!sendEmail || tpl) return;
    let dead = false;
    panelGet('users', 'bulk_invite_template').then(({ data }) => {
      if (!dead && data) { setTpl(data); setTplOpen(true); }
    });
    return () => { dead = true; };
  }, [sendEmail, tpl]);

  const parseCsv = (text: string) => {
    // Simple CSV: header row maps by name (case/space tolerant) to BULK_COLS.
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    const split = (l: string) => {
      const out: string[] = []; let cur = ''; let inQ = false;
      for (let i = 0; i < l.length; i++) {
        const ch = l[i];
        if (inQ) {
          if (ch === '"' && l[i + 1] === '"') { cur += '"'; i++; }
          else if (ch === '"') inQ = false;
          else cur += ch;
        } else if (ch === '"') inQ = true;
        else if (ch === ',') { out.push(cur); cur = ''; }
        else cur += ch;
      }
      out.push(cur);
      return out;
    };
    const header = split(lines[0]).map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    return lines.slice(1).map(l => {
      const vals = split(l);
      const row: Record<string, string> = {};
      header.forEach((h, i) => { if (BULK_COLS.includes(h)) row[h] = (vals[i] || '').trim(); });
      return row;
    }).filter(r => r.email);
  };

  const onFile = (file: File) => {
    setFileName(file.name);
    file.text().then(t => {
      const parsed = parseCsv(t);
      setRows(parsed);
      setMsg(parsed.length
        ? { kind: 'ok', text: `${parsed.length} row(s) parsed — review below, then upload.` }
        : { kind: 'error', text: 'No usable rows found. The CSV needs a header row with an "email" column.' });
    });
  };

  const template = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(
      [BULK_COLS.join(',') + '\njane@example.com,Jane,Doe,India,Acme Labs,CTO,https://linkedin.com/in/jane'],
      { type: 'text/csv' }));
    a.download = 'bulk_users_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const upload = async () => {
    setBusy(true); setMsg(null); setTracked(null);
    const { data, error } = await panelPost('users', 'bulk_create',
      { rows, send_email: sendEmail });
    setBusy(false);
    if (error) setMsg({ kind: 'error', text: error });
    else {
      if (Number(data.emailed) > 0 && data.run_id) setTracked({ run_id: String(data.run_id), emailed: Number(data.emailed) });
      const errs = (data.errors || []).slice(0, 5).join(' · ');
      // Email outcome is reported separately from the account outcome: a
      // failed send does not undo a created account, so conflating them would
      // misreport what actually happened.
      const mail = data.email_requested
        ? ` Emailed ${data.emailed}.`
          + ((data.email_failed || []).length
            ? ` Failed for ${data.email_failed.length}: ${(data.email_failed || []).slice(0, 3).join(' · ')}`
            : '')
        : '';
      setMsg({
        kind: data.created ? 'ok' : 'error',
        text: `Created ${data.created}, skipped ${data.skipped} existing.${mail}`
          + (errs ? ` Errors: ${errs}` : ''),
      });
      if (data.created) setRows([]);
    }
  };

  return (
    <Section title="Bulk add users from CSV" right={<Btn small onClick={template}>Download template</Btn>}>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {tracked && (
        <div className="text-xs">
          <AdminLink to={{ group: 'email', tab: 'invites', run: tracked.run_id }}
            title={`Delivered, opened, clicked and signed in, for the ${tracked.emailed} invitation e-mail(s) just sent`}>
            Track these invites in Email Center →</AdminLink>
        </div>
      )}
      <input type="file" accept=".csv" className="text-sm"
        onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} />
      {fileName && <div className="text-xs text-grayscale-1100">{fileName}</div>}
      {rows.length > 0 && (
        <>
          <DataTable rows={rows.slice(0, 100)} columns={BULK_COLS} maxHeight="18rem" />

          <label className="text-sm flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={sendEmail}
              onChange={e => { setSendEmail(e.target.checked); if (!e.target.checked) setTplOpen(false); }} />
            Send the invitation email to each user created
          </label>
          {sendEmail && (
            <div className="border border-primary-200 rounded-xl bg-white">
              <button type="button" onClick={() => setTplOpen(o => !o)}
                className="w-full text-left px-3 py-2 text-xs text-primary-700">
                {tplOpen ? '▾' : '▸'} This is the email that will be sent
                {tpl?.name ? ` — ${tpl.name}` : ''}
                {tpl?.edited ? ' (edited in Email Center)' : ''}
              </button>
              {tplOpen && (
                tpl ? (
                  <div className="px-3 pb-3 flex flex-col gap-1.5 text-xs">
                    <div className="text-grayscale-1100">From: {tpl.from}</div>
                    <div className="text-grayscale-1100">Reply-To: {tpl.reply_to}</div>
                    <div><span className="text-grayscale-1100">Subject:</span>{' '}
                      <span className="font-medium">{tpl.subject}</span></div>
                    {/* Read-only on purpose: this screen previews the copy, it
                        does not own it. Editing lives in Email Center so one
                        template cannot be changed from two places. */}
                    <pre className="whitespace-pre-wrap font-mono text-[11px] bg-grayscale-100 rounded-md p-2 max-h-64 overflow-y-auto">
                      {tpl.body}
                    </pre>
                    <div className="text-[11px] text-grayscale-1100">
                      Edit this copy in Email Center → Templates (editable).
                    </div>
                  </div>
                ) : <div className="px-3 pb-3 text-xs text-grayscale-1100">Loading the template…</div>
              )}
            </div>
          )}

          <div>
            <Btn kind="primary" disabled={busy} onClick={upload}>
              {busy ? 'Creating…'
                : `Create ${rows.length} user(s)${sendEmail ? ' and email them' : ''}`}
            </Btn>
          </div>
        </>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Invitations — the quick send form, the fuller request form (designation +
// referrer, the fields the Streamlit hardcoded blank) and the full request log.
// ---------------------------------------------------------------------------
const mergeOpts = (known: string[], seen: any[]) => {
  const next = new Set(known);
  seen.forEach(v => { if (v) next.add(String(v)); });
  return next.size === known.length ? known : Array.from(next).sort();
};

// ---------------------------------------------------------------------------
// Send invites — reach accounts that were created without an invitation email
// ---------------------------------------------------------------------------
/**
 * Bulk add creates accounts; until recently it never mailed them, so imports
 * exist whose users were never told they have an account. Re-uploading the CSV
 * does not fix it — every row comes back "already exists" and a skipped row is
 * never mailed — so this is the way back to them.
 *
 * The flow is deliberately preview-first: pick an import, look at exactly who
 * would be mailed and why anyone is excluded, read the email itself, then send.
 * The ids the admin confirms are the ids that get mailed; nothing is
 * re-resolved at send time.
 */
// ---------------------------------------------------------------------------
// Sign-in — what a Google / LinkedIn sign-in does for an address with no account
// ---------------------------------------------------------------------------
const SignInSettings: React.FC = () => {
  const st = useAdminGet('users', 'signin_settings');
  const [msg, setMsg] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  const d = st.data;
  const on = !!d?.enabled;
  const counts = d?.oauth_accounts || {};

  const save = async (enabled: boolean) => {
    setBusy(true);
    const { data, error } = await panelPost('users', 'signin_settings_set', { enabled });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: data?.enabled
      ? 'On — a new Google or LinkedIn sign-in now creates the account straight away.'
      : 'Off — a new Google or LinkedIn sign-in now asks “No account found — create one?” first.' });
    st.reload();
  };

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Google / LinkedIn sign-in with a new address"
        right={<Btn small onClick={st.reload}>Refresh</Btn>}>
        {st.loading && !d ? <Loading /> : st.error ? <Msg kind="error">{st.error}</Msg> : (
          <div className="flex flex-col gap-3">
            <label className="flex items-start gap-3 text-sm text-grayscale-1200 cursor-pointer">
              <input type="checkbox" className="mt-1" checked={on} disabled={busy}
                onChange={e => save(e.target.checked)} />
              <span>
                <span className="font-medium text-grayscale-1300">Create the account straight away</span>
                <span className="block text-xs text-grayscale-1100 mt-0.5">
                  On: someone who signs in with Google or LinkedIn and has no account gets one at once,
                  with the welcome e-mail and onboarding, as if they had used Sign up.
                  Off: they see “No Account Found — would you like to create a new account?” and choose
                  Yes or Cancel.
                </span>
              </span>
              <span className="ml-auto">{on ? <Pill tone="ok">On</Pill> : <Pill tone="mute">Off</Pill>}</span>
            </label>
            <div className="text-xs text-grayscale-1100">
              {d?.default ? 'Not changed yet (on by default).'
                : <>Last changed {d?.at ? fmtIST(d.at) : '—'}{d?.by ? ` by ${d.by}` : ''}.</>}
              {' '}E-mail and password sign-in is unaffected: a new address there is pointed to Sign up.
              Suspended accounts and addresses moved to a new e-mail are refused either way.
            </div>
            {counts.all_time != null && (
              <KPIRow>
                <KPI label="Accounts made by Google / LinkedIn, last 30 days" value={counts.last_30_days} />
                <KPI label="Since tracking began" value={counts.all_time} />
              </KPIRow>
            )}
          </div>
        )}
      </Section>
    </div>
  );
};

const SendInvites: React.FC = () => {
  const batches = useAdminGet<any>('users', 'invite_batches', { limit: 10 });
  const [win, setWin] = useState<{ from: string; to: string; label: string } | null>(null);
  const [cand, setCand] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [showTpl, setShowTpl] = useState(false);
  const [delay, setDelay] = useState(2);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Flash>(null);
  const [run, setRun] = useState<{ campaign_id: string; queued: number } | null>(null);
  const [prog, setProg] = useState<any>(null);

  const tpl = useAdminGet<any>('users', 'bulk_invite_template');

  const load = async (from: string, to: string, label: string) => {
    setLoading(true); setMsg(null); setCand(null); setRun(null); setProg(null);
    const { data, error } = await panelGet('users', 'invite_candidates',
      { created_from: from, created_to: to, limit: 500 });
    setLoading(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setWin({ from, to, label });
    setCand(data);
    // Everything eligible starts ticked — the admin's job is to remove people,
    // not to re-select the batch they just chose.
    const next: Record<string, boolean> = {};
    (data?.rows || []).forEach((r: any) => { if (r.eligible) next[r.id] = true; });
    setPicked(next);
  };

  const rows = cand?.rows || [];
  const ids = Object.keys(picked).filter(k => picked[k]);

  // Progress is polled from the ledger while a run is live. The interval is
  // cleared on unmount so leaving the tab does not leave a timer behind.
  useEffect(() => {
    if (!run?.campaign_id) return;
    let dead = false;
    let t: any = null;
    const tick = async () => {
      const { data } = await panelGet('users', 'invite_send_status',
        { campaign_id: run.campaign_id });
      if (dead || !data) return;
      setProg(data);
      // Stop on the ledger count, not on data.running: the sender's
      // in-flight flag lives in one worker process, so a status call routed to
      // a different worker reports "not running" while the send is still
      // going. The row count is shared state and cannot lie about it.
      const finished = data.done >= run.queued || (!data.running && data.done > 0);
      if (finished && t) { clearInterval(t); t = null; }
    };
    tick();
    t = setInterval(tick, 4000);
    return () => { dead = true; if (t) clearInterval(t); };
  }, [run?.campaign_id]);

  const send = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost('users', 'send_invites',
      { user_ids: ids, delay_sec: delay, confirm: true });
    setBusy(false);
    if (error || !data) { setMsg({ kind: 'error', text: error || 'Send failed' }); return; }
    setRun({ campaign_id: data.campaign_id, queued: data.queued || 0 });
    setAck(false);
    setMsg({ kind: 'ok',
      text: `Started. ${data.queued} queued at ${data.delay_sec}s apart`
        + ` (about ${Math.ceil((data.eta_sec || 0) / 60)} min).`
        + (data.skipped_already_sent ? ` ${data.skipped_already_sent} skipped as already invited.` : '') });
  };

  const CAND_COLS = ['email', 'first_name', 'last_name', 'created_at', 'skip_reason'];
  const ineligible = rows.filter((r: any) => !r.eligible);
  // Already invited — by an earlier Send invites run or by Bulk add's own
  // invitation (invite_backfill counts both ledgers since 2026-10-15).
  const already = ineligible.filter((r: any) => r.invited_at || /already sent/i.test(String(r.skip_reason || '')));
  // "on 15 Oct 2026", or "between 2 Sep 2026 and 15 Oct 2026" (India time).
  const invitedOn = (() => {
    const days = Array.from(new Set(already.map((r: any) => String(r.invited_at || ''))
      .filter((v: string) => v && !Number.isNaN(Date.parse(v)))
      .sort((a: string, b: string) => Date.parse(a) - Date.parse(b))
      .map((v: string) => fmtIST(v).replace(/,?\s*\d{1,2}:\d{2}.*$/, ''))));
    if (!days.length) return '';
    return days.length === 1 ? `on ${days[0]}` : `between ${days[0]} and ${days[days.length - 1]}`;
  })();

  return (
    <div className="flex flex-col gap-4">
      <Section title="1 · Pick the import">
        {batches.loading && <Loading label="Loading recent bulk adds…" />}
        {batches.error && <Msg kind="error">{batches.error}</Msg>}
        {!batches.loading && !(batches.data?.rows || []).length && (
          <div className="text-xs text-grayscale-1100">
            No bulk-add runs recorded yet.
            {batches.data?.error ? ` (${batches.data.error})` : ''}
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          {(batches.data?.rows || []).map((b: any) => {
            const invited = Number(b.invited) || 0;
            const all = invited > 0 && invited >= (Number(b.nearby_users) || 0);
            return (
              <div key={b.id} className="flex items-center gap-3 text-xs flex-wrap">
                <Btn small onClick={() => load(b.created_from, b.created_to,
                  `${fmtDate(b.created_at)} · ${b.admin_email}`)}>
                  Load
                </Btn>
                <span className="text-grayscale-1200">{fmtDate(b.created_at)}</span>
                <span className="text-grayscale-1100">{b.admin_email}</span>
                <span className="text-grayscale-1100">
                  {b.nearby_users} user(s)
                  {invited > 0 && (
                    <span className="text-amber-700" title="Already sent the invitation (by Bulk add or an earlier Send invites run)">
                      {' '}· {all ? 'all' : invited} already invited
                    </span>
                  )}
                </span>
                {b.group_id && invited > 0 && (
                  <AdminLink to={{ group: 'email', tab: 'invites', bulk: String(b.group_id) }}
                    title="Delivered, opened, clicked and signed in for this import's invites"
                    className="text-primary-700 underline underline-offset-2 whitespace-nowrap">
                    Tracking →</AdminLink>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {loading && <Loading label="Resolving who would be mailed…" />}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      {cand && (
        <>
          <Section title={`2 · Who gets the email — ${win?.label || ''}`}
            right={<span className="text-xs text-grayscale-1100">
              {ids.length} selected of {cand.eligible} eligible ({cand.total} in the import)
            </span>}>
            {cand.capped && (
              <Msg kind="error">
                This import is larger than one run allows — only the first {cand.total} are
                shown. Send these, then load it again for the rest.
              </Msg>
            )}
            {already.length > 0 && Number(cand.eligible) === 0 && (
              <Notice>
                Everyone in this import was already invited{invitedOn ? ` ${invitedOn}` : ''}. Sending again would
                mail them a second time.
              </Notice>
            )}
            {ineligible.length > 0 && (
              <div className="text-xs text-grayscale-1100">
                {ineligible.length} excluded:{' '}
                {Object.entries(
                  ineligible.reduce((a: Record<string, number>, r: any) => {
                    a[r.skip_reason] = (a[r.skip_reason] || 0) + 1; return a;
                  }, {})
                ).map(([k, v]) => `${v} ${k}`).join(' · ')}
              </div>
            )}
            <div className="flex gap-2">
              <Btn small onClick={() => {
                const n: Record<string, boolean> = {};
                rows.forEach((r: any) => { if (r.eligible) n[r.id] = true; });
                setPicked(n);
              }}>Select all eligible</Btn>
              <Btn small onClick={() => setPicked({})}>Clear</Btn>
            </div>
            <div className="max-h-80 overflow-y-auto border border-primary-200 rounded-xl">
              <table className="w-full text-xs">
                <tbody>
                  {rows.map((r: any) => (
                    <tr key={r.id} className="border-b border-primary-100 last:border-0">
                      <td className="p-1.5 w-8">
                        <input type="checkbox" disabled={!r.eligible}
                          checked={!!picked[r.id]}
                          onChange={e => setPicked(p => ({ ...p, [r.id]: e.target.checked }))} />
                      </td>
                      <td className="p-1.5">{r.email}</td>
                      <td className="p-1.5 text-grayscale-1100">
                        {[r.first_name, r.last_name].filter(Boolean).join(' ')}
                      </td>
                      <td className="p-1.5 text-grayscale-1100">
                        {r.skip_reason || ''}
                        {!r.eligible && r.invited_at
                          ? <span className="text-amber-700"> · {fmtIST(r.invited_at)}</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="3 · The email they will receive"
            right={<Btn small onClick={() => setShowTpl(s => !s)}>
              {showTpl ? 'Hide' : 'Show'}
            </Btn>}>
            {showTpl && (tpl.data ? (
              <div className="flex flex-col gap-1.5 text-xs">
                <div className="text-grayscale-1100">From: {tpl.data.from}</div>
                <div className="text-grayscale-1100">Reply-To: {tpl.data.reply_to}</div>
                <div><span className="text-grayscale-1100">Subject:</span>{' '}
                  <span className="font-medium">{tpl.data.subject}</span></div>
                <pre className="whitespace-pre-wrap font-mono text-[11px] bg-grayscale-100 rounded-md p-2 max-h-64 overflow-y-auto">
                  {tpl.data.body}
                </pre>
                <div className="text-[11px] text-grayscale-1100">
                  Edit this copy in Email Center → Templates (editable). The HTML
                  part is generated from this same text, so what you read here is
                  what lands.
                </div>
              </div>
            ) : <Loading label="Loading the template…" />)}
          </Section>

          <Section title="4 · Send">
            <div className="flex items-end gap-3">
              <label className="text-xs text-grayscale-1100 flex flex-col gap-1">
                Seconds between sends
                <TextInput type="number" value={String(delay)}
                  onChange={v => setDelay(Math.max(0, Math.min(60, Number(v) || 0)))} />
              </label>
              <div className="text-xs text-grayscale-1100 pb-2">
                {ids.length} email(s) · about {Math.ceil((ids.length * (delay + 0.5)) / 60)} min
              </div>
            </div>
            <Check checked={ack} onChange={setAck}>
              I have read the email above and want to send it to these {ids.length} people.
              This cannot be undone.
            </Check>
            <div>
              <Btn kind="primary" disabled={!ack || !ids.length || busy} onClick={send}>
                {busy ? 'Starting…' : `Send ${ids.length} invitation email(s)`}
              </Btn>
            </div>
            {prog && (
              <div className="text-xs flex flex-col gap-1">
                <div>
                  {prog.done >= (run?.queued || 0) ? 'Finished.' : 'Sending…'}{' '}
                  {prog.sent} sent{prog.failed ? `, ${prog.failed} failed` : ''}
                  {run?.queued ? ` of ${run.queued}` : ''}.
                </div>
                {prog.failed > 0 && (
                  <div className="text-grayscale-1100">
                    {(prog.rows || []).filter((r: any) => r.status === 'failed')
                      .slice(0, 5).map((r: any) => `${r.researcher_email}: ${r.error || ''}`)
                      .join(' · ')}
                  </div>
                )}
              </div>
            )}
            {run?.campaign_id && (
              <div className="text-xs">
                <AdminLink to={{ group: 'email', tab: 'invites', run: run.campaign_id }}
                  title="Delivered, opened, clicked and signed in for this run (fills in as SendGrid reports)">
                  Track opens and clicks in Email Center →</AdminLink>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
};

const Invitations: React.FC = () => {
  const [invStatus, setInvStatus] = useState('All');
  const [mailStatus, setMailStatus] = useState('All');
  const [qRaw, setQRaw] = useState('');
  const q = useDebounced(qRaw);
  const [msg, setMsg] = useState<Flash>(null);

  const params = useMemo(() => {
    const p: Record<string, string | number> = { limit: 200 };
    if (invStatus !== 'All') p.invite_status = invStatus;
    if (mailStatus !== 'All') p.email_status = mailStatus;
    if (q.trim()) p.q = q.trim();
    return p;
  }, [invStatus, mailStatus, q]);
  const list = useAdminGet('users', 'invite_requests', params, [invStatus, mailStatus, q]);

  const rows: any[] = list.data?.rows || [];
  const stats = list.data?.stats || {};

  // Filter vocabularies come from the data itself so they cannot drift from the
  // backend; the first (unfiltered) load seeds them.
  const [invOpts, setInvOpts] = useState<string[]>([]);
  const [mailOpts, setMailOpts] = useState<string[]>([]);
  useEffect(() => {
    setInvOpts(o => mergeOpts(o, rows.map(r => r.invite_status)));
    setMailOpts(o => mergeOpts(o, rows.map(r => r.email_status)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data]);

  const setStatus = async (id: string, status: string) => {
    const { error } = await panelPost('users', 'invite_status', { invite_id: id, status });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `Marked ${status}.` }); list.reload(); }
  };

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Shown" value={stats.total ?? rows.length}
          hint={stats.total_all != null ? `${stats.total_all} all-time` : undefined} />
        <KPI label="Pending" value={stats.pending ?? '—'} />
        <KPI label="Email sent" value={stats.email_sent ?? '—'} />
        <KPI label="Responded" value={stats.responded ?? '—'} />
      </KPIRow>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <QuickInvite onSent={t => { setMsg({ kind: 'ok', text: t }); list.reload(); }} />
        <FullInviteRequest onSent={t => { setMsg({ kind: 'ok', text: t }); list.reload(); }} />
      </div>
      {list.error && <Msg kind="error">{list.error}</Msg>}
      <Section title="All invitation requests" right={<Btn small onClick={list.reload}>Refresh</Btn>}>
        <div className="flex gap-2 flex-wrap items-center">
          <TextInput value={qRaw} onChange={setQRaw}
            placeholder="Search email / organisation / name…" wide />
          <Select value={invStatus} onChange={setInvStatus} options={['All', ...invOpts]}
            labels={{ All: 'All invite statuses' }} />
          <Select value={mailStatus} onChange={setMailStatus} options={['All', ...mailOpts]}
            labels={{ All: 'All email statuses' }} />
          <span className="text-xs text-grayscale-1100">
            Showing {list.data?.shown ?? rows.length}
            {stats.total_all != null ? ` of ${stats.total_all}` : ''}
          </span>
        </div>
        {list.loading ? <Loading /> : (
          <DataTable rows={rows} csvName="invite_requests"
            columns={['invitee_email', 'name', 'organisation', 'designation', 'country',
              'linkedin_url', 'invite_status', 'email_status', 'inviter_name', 'sent_at',
              'responded_at', 'last_email_event_at', 'message']}
            labels={{
              invitee_email: 'Email', inviter_name: 'Invited by', linkedin_url: 'LinkedIn',
              last_email_event_at: 'Last email event',
            }}
            empty="No invitation requests match these filters."
            render={{
              linkedin_url: r => LinkCell(r),
              invite_status: r => <Pill tone={r.responded ? 'ok' : 'mute'}>{r.invite_status || '—'}</Pill>,
              inviter_name: r => <span title={r.inviter_email}>{r.inviter_name || r.inviter_email || '—'}</span>,
            }}
            actions={r => (
              <div className="flex gap-1">
                {r.invite_status !== 'Approved' && <Btn small onClick={() => setStatus(r.id, 'Approved')}>Approve</Btn>}
                {r.invite_status !== 'Declined' && <Btn small onClick={() => setStatus(r.id, 'Declined')}>Decline</Btn>}
              </div>
            )} />
        )}
      </Section>
    </div>
  );
};

const QuickInvite: React.FC<{ onSent: (text: string) => void }> = ({ onSent }) => {
  const [f, setF] = useState({ email: '', first_name: '', message: '' });
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true); setErr('');
    const { data, error } = await panelPost('users', 'invite_create', { ...f, confirm: true });
    setBusy(false);
    if (error) setErr(error);
    else {
      onSent(data?.message || `Invitation sent to ${f.email}.`);
      setF({ email: '', first_name: '', message: '' });
      setConfirm(false);
    }
  };

  return (
    <Section title="Quick invite">
      {err && <Msg kind="error">{err}</Msg>}
      <div className="flex gap-2 flex-wrap items-end">
        <TextInput value={f.email} onChange={v => setF(s => ({ ...s, email: v }))} placeholder="email@company.com" />
        <TextInput value={f.first_name} onChange={v => setF(s => ({ ...s, first_name: v }))} placeholder="First name" />
        <TextInput value={f.message} onChange={v => setF(s => ({ ...s, message: v }))} placeholder="Optional message" wide />
      </div>
      <Check checked={confirm} onChange={setConfirm}>
        Send this invitation email now — it cannot be recalled.
      </Check>
      <div>
        <Btn kind="primary" disabled={busy || !confirm || !f.email} onClick={send}>
          {busy ? 'Sending…' : 'Send invite'}
        </Btn>
      </div>
    </Section>
  );
};

/** users/invite_request — the only path that writes the row feeding Approvals. */
const FullInviteRequest: React.FC<{ onSent: (text: string) => void }> = ({ onSent }) => {
  const empty = {
    email: '', first_name: '', last_name: '', country: '', organisation: '',
    designation: '', linkedin_url: '', referred_by_email: '',
  };
  const [f, setF] = useState<Record<string, string>>(empty);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<any>(null);

  const send = async () => {
    setBusy(true); setErr(''); setDetail(null);
    const { data, error } = await panelPost('users', 'invite_request', { ...f, confirm: true });
    setBusy(false);
    if (error) setErr(error);
    else {
      setDetail(data?.detail || null);
      onSent(data?.message || `Invitation request recorded for ${f.email}.`);
      setF(empty);
      setConfirm(false);
    }
  };

  const field = (k: string, label: string, type = 'text') => (
    <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
      {label}
      <input type={type} value={f[k]} onChange={e => setF(s => ({ ...s, [k]: e.target.value }))}
        className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
    </label>
  );

  return (
    <Section title="Full invitation request">
      <p className="text-xs text-grayscale-1100 -mt-1">
        Writes the approval row that shows up under <b>Approvals</b>, and mails the invitation.
        Designation and referrer are recorded here — the old Streamlit form left them blank.
      </p>
      {err && <Msg kind="error">{err}</Msg>}
      <div className="grid grid-cols-2 gap-3">
        {field('email', 'Email *', 'email')}
        {field('first_name', 'First name')}
        {field('last_name', 'Last name')}
        {field('organisation', 'Organisation')}
        {field('designation', 'Designation')}
        {field('country', 'Country')}
        {field('linkedin_url', 'LinkedIn URL')}
        {field('referred_by_email', 'Referred by (email)', 'email')}
      </div>
      <Check checked={confirm} onChange={setConfirm}>
        Record the request and send the invitation email — it cannot be recalled.
      </Check>
      <div>
        <Btn kind="primary" disabled={busy || !confirm || !f.email} onClick={send}>
          {busy ? 'Sending…' : 'Create request & invite'}
        </Btn>
      </div>
      {detail && (
        <pre className="text-xs bg-grayscale-200 rounded-lg p-3 overflow-auto max-h-40 text-grayscale-1200">
          {JSON.stringify(detail, null, 2)}
        </pre>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const LC_PAGE = 100;

const LinkedInCache: React.FC = () => {
  const [qRaw, setQRaw] = useState('');
  const q = useDebounced(qRaw);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<any>(null);

  useEffect(() => { setPage(0); }, [q]);

  const params = useMemo(() => {
    const p: Record<string, string | number> = { limit: LC_PAGE, offset: page * LC_PAGE };
    if (q.trim()) p.q = q.trim();
    return p;
  }, [q, page]);
  const cache = useAdminGet('users', 'linkedin_cache', params, [q, page]);

  const rows: any[] = cache.data?.rows || [];
  const s = cache.data?.summary || {};
  const matched = s.matched ?? rows.length;
  const pages = Math.max(1, Math.ceil((matched || 0) / LC_PAGE));

  if (cache.data && cache.data.available === false) {
    return (
      <Section title="LinkedIn profile cache">
        <Notice>
          The <code>linkedin_profiles</code> table does not exist in this database, so nothing has
          been cached yet. It is created the first time a profile is enriched.
        </Notice>
      </Section>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Cached profiles" value={s.total ?? '—'} />
        <KPI label="Last 7 days" value={s.recent_7d ?? '—'} />
        <KPI label="Last 30 days" value={s.recent_30d ?? '—'} />
        <KPI label="Unlinked" value={s.unlinked ?? '—'} hint="no user attached" />
        <KPI label="No professional summary" value={s.missing_professional ?? '—'} />
        <KPI label="No R&D summary" value={s.missing_rd ?? '—'} />
      </KPIRow>
      {cache.error && <Msg kind="error">{cache.error}</Msg>}
      <Section title="Cached BrightData profiles" right={<Btn small onClick={cache.reload}>Refresh</Btn>}>
        <div className="flex gap-2 flex-wrap items-center">
          <TextInput value={qRaw} onChange={setQRaw}
            placeholder="Search URL / user email / summary text…" wide />
          <span className="text-xs text-grayscale-1100">
            {matched.toLocaleString?.() ?? matched} match(es){q.trim() ? ` for “${q.trim()}”` : ''}
          </span>
        </div>
        {cache.loading ? <Loading /> : (
          <DataTable rows={rows} csvName="linkedin_cache"
            columns={['linkedin_url', 'user_email', 'professional_summary', 'rd_summary',
              'has_professional', 'has_rd', 'has_raw', 'fetched_at', 'updated_at']}
            labels={{
              linkedin_url: 'LinkedIn', user_email: 'User', professional_summary: 'Professional summary',
              rd_summary: 'R&D summary', has_professional: 'Prof.', has_rd: 'R&D', has_raw: 'Raw',
            }}
            empty="Nothing cached for this search."
            render={{
              linkedin_url: r => LinkCell(r),
              user_email: r => r.user_email
                ? <span>{r.user_email}</span> : <Pill tone="warn">unlinked</Pill>,
            }}
            actions={r => <Btn small onClick={() => setOpen(r)}>Open</Btn>} />
        )}
        <div className="flex items-center gap-2 justify-end text-xs text-grayscale-1100">
          <Btn small disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Btn>
          Page {page + 1} of {pages}
          <Btn small disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next</Btn>
        </div>
      </Section>
      {open && <LinkedInDrawer row={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const LinkedInDrawer: React.FC<{ row: any; onClose: () => void }> = ({ row, onClose }) => {
  // id wins over the URL: the same profile can be refetched, so the URL is not unique.
  const prof = useAdminGet('users', 'linkedin_profile', { id: row.id }, [row.id]);
  const p = prof.data?.profile || {};
  const raw = p.raw_json;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="bg-grayscale-100 w-full max-w-2xl h-full overflow-y-auto p-6 flex flex-col gap-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">
              {p.user_email || row.user_email || 'Unlinked profile'}
            </h2>
            <div className="text-xs text-grayscale-1100">
              Fetched {fmtDate(p.fetched_at || row.fetched_at)} · updated {fmtDate(p.updated_at || row.updated_at)}
            </div>
          </div>
          <Btn onClick={onClose}>Close</Btn>
        </div>
        {prof.error && <Msg kind="error">{prof.error}</Msg>}
        {prof.loading ? <Loading /> : prof.data?.found === false ? (
          <Notice>This cache row no longer exists — it was refetched or purged. Refresh the table.</Notice>
        ) : (
          <>
            <Section title="Profile">
              <div className="text-sm text-grayscale-1300">{LinkCell(p.linkedin_url ? p : row)}</div>
              <div className="text-xs text-grayscale-1100">
                Row {p.id ?? row.id}{p.user_id ? ` · user ${p.user_id}` : ' · not linked to a user'}
              </div>
            </Section>
            <Section title="Professional summary">
              <p className="text-sm text-grayscale-1300 whitespace-pre-wrap">
                {p.professional_summary || <span className="text-grayscale-1100">Not generated.</span>}
              </p>
            </Section>
            <Section title="R&D summary">
              <p className="text-sm text-grayscale-1300 whitespace-pre-wrap">
                {p.rd_summary || <span className="text-grayscale-1100">Not generated.</span>}
              </p>
            </Section>
            <Section title="Raw BrightData payload">
              {raw ? (
                <pre className="text-xs bg-grayscale-200 rounded-lg p-3 overflow-auto max-h-[28rem] text-grayscale-1200 whitespace-pre-wrap break-words">
                  {JSON.stringify(raw, null, 2)}
                </pre>
              ) : (
                <div className="text-sm text-grayscale-1100">No raw payload stored for this row.</div>
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Deletion requests — the settings card for the account_deletion scheduler,
// the request queue with its Hold / Resume / Cancel / Delete-now interventions,
// and the archive of every account already deleted. Handlers live in
// Backend/account_deletion.py (users/deletion_*); the three legacy core keys
// (deletion_requests / deletion_cancel / deletion_complete) are re-pointed
// there too, so this component only ever talks to that module.
// ---------------------------------------------------------------------------

/** Ranges enforced by account_deletion.set_settings — mirrored so the form can
 *  say so before a round trip. */
const DELETION_DAYS = { min: 1, max: 365 };
const DELETION_EVERY = { min: 5, max: 1440 };

/** Status → pill tone. `cancelled` deliberately has no entry: it renders as
 *  plain muted text so a closed request is visibly quieter than a live one.
 *  `deleting` is the seconds between archive and cascade — amber, no actions. */
const DELETION_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  pending: 'mute', on_hold: 'warn', deleting: 'warn', completed: 'ok', failed: 'bad',
};
const deletionPill = (status: any) => {
  const s = String(status || '').toLowerCase();
  if (!s) return <span className="text-grayscale-1100">—</span>;
  if (s === 'cancelled') return <span className="text-[11px] text-grayscale-1100">cancelled</span>;
  return <Pill tone={DELETION_TONE[s] || 'mute'}>{s.replace('_', ' ')}</Pill>;
};

const fmtBytes = (n: any): string => {
  const b = Number(n);
  if (n === null || n === undefined || isNaN(b)) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

/** "in 3 days" / "due today" / "overdue 2 days" from the server's days_left
 *  (negative once the due date has passed). */
const dueLabel = (daysLeft: any): string => {
  const n = Number(daysLeft);
  if (daysLeft === null || daysLeft === undefined || isNaN(n)) return '';
  if (n >= 0.5) { const d = Math.round(n); return `in ${d} day${d === 1 ? '' : 's'}`; }
  if (n <= -0.5) { const d = Math.round(-n); return `overdue ${d} day${d === 1 ? '' : 's'}`; }
  return 'due today';
};

/** process_due's `failed` entries may be emails or {email, error} objects. */
const failedLabel = (x: any): string =>
  typeof x === 'string' ? x : x?.email ? `${x.email}${x.error ? ` (${x.error})` : ''}` : JSON.stringify(x);

/** What the details column says for CSV export and the table's own search;
 *  the on-screen cell is richer (an archive download button). */
const deletionDetailText = (r: Record<string, any>): string => {
  switch (r.status) {
    case 'on_hold': return `${r.hold_reason || 'no reason given'} — ${r.held_by || '?'}`;
    case 'completed': return `by ${r.deleted_by || '—'}${r.archive_id ? ` · archive ${r.archive_id}` : ''}`;
    case 'failed': return r.last_error || 'failed';
    case 'deleting': return 'archived — cascade running';
    case 'cancelled': return r.cancelled_at ? `cancelled ${fmtDate(r.cancelled_at)}` : 'cancelled';
    default: return r.user_exists === false ? 'user already gone' : '';
  }
};

const DeletionRequests: React.FC = () => {
  const status = useAdminGet('users', 'deletion_settings');
  const reqs = useAdminGet('users', 'deletion_requests');
  const [msg, setMsg] = useState<Flash>(null);
  // Bumped after anything that can add an archive row, so the archive table
  // refetches without a full page reload.
  const [archiveTick, setArchiveTick] = useState(0);
  const [busy, setBusy] = useState('');

  // Settings form. `dirty` keeps a half-typed edit from being overwritten by
  // the status refetch that follows every queue action.
  const [auto, setAuto] = useState(true);
  const [days, setDays] = useState('7');
  const [every, setEvery] = useState('60');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    const s = status.data?.settings;
    if (!s || dirty) return;
    setAuto(!!s.auto_delete);
    setDays(String(s.days ?? 7));
    setEvery(String(s.interval_minutes ?? 60));
  }, [status.data, dirty]);

  const reloadAll = () => {
    reqs.reload(); status.reload();
    // Tell the page-wide banner too (AdminPage polls only once a minute).
    document.dispatchEvent(new Event('utech:deletion-alerts-changed'));
  };
  const afterDeletion = () => { reloadAll(); setArchiveTick(t => t + 1); };

  const saveSettings = async () => {
    const d = Number(days), e = Number(every);
    if (!Number.isInteger(d) || d < DELETION_DAYS.min || d > DELETION_DAYS.max) {
      setMsg({ kind: 'error', text: `Days must be a whole number from ${DELETION_DAYS.min} to ${DELETION_DAYS.max}.` });
      return;
    }
    if (!Number.isInteger(e) || e < DELETION_EVERY.min || e > DELETION_EVERY.max) {
      setMsg({ kind: 'error', text: `Check interval must be ${DELETION_EVERY.min}–${DELETION_EVERY.max} minutes.` });
      return;
    }
    const { data, error } = await panelPost('users', 'deletion_settings_set',
      { auto_delete: auto, days: d, interval_minutes: e });
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    // The response is the merged settings the server stored. Put them in the
    // form BEFORE clearing dirty: React batches these with reloadAll(), and
    // the sync effect would otherwise copy the OLD status.data back into the
    // inputs until the refetch lands (or for good, if it fails).
    if (data && typeof data === 'object') {
      setAuto(!!data.auto_delete);
      setDays(String(data.days ?? d));
      setEvery(String(data.interval_minutes ?? e));
    }
    setDirty(false);
    setMsg({ kind: 'ok', text: data?.auto_delete
      ? `Saved — accounts are deleted automatically ${data.days} days after the request, checked every ${data.interval_minutes} min.`
      : 'Saved — automatic deletion is off; requests wait for Delete now or Run now.' });
    reloadAll();
  };

  const runNow = async () => {
    setBusy('run');
    const { data, error } = await panelPost('users', 'deletion_run_now', {});
    setBusy('');
    if (error) setMsg({ kind: 'error', text: error });
    else if (data?.skipped) setMsg({ kind: 'error', text: String(data.skipped) });
    else {
      const deleted: any[] = data?.deleted || [];
      const failed: any[] = data?.failed || [];
      setMsg({ kind: failed.length ? 'error' : 'ok',
        text: `Checked ${data?.checked ?? 0} due request${data?.checked === 1 ? '' : 's'} — `
          + `deleted ${deleted.length}${deleted.length ? ` (${deleted.join(', ')})` : ''}, `
          + `failed ${failed.length}${failed.length ? ` (${failed.map(failedLabel).join('; ')})` : ''}.` });
    }
    afterDeletion();
  };

  const hold = async (r: Record<string, any>) => {
    const reason = window.prompt(`Put ${r.email} on hold — why? (kept on the request)`, '');
    if (reason === null) return;
    const { error } = await panelPost('users', 'deletion_hold', { request_id: r.id, reason: reason.trim() });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `${r.email} is on hold — nothing happens until you resume or cancel.` }); reloadAll(); }
  };
  // Also the Retry for a failed request: the server clears last_error and
  // puts it back on the clock, so the next sweep tries again.
  const resume = async (r: Record<string, any>) => {
    const { error } = await panelPost('users', 'deletion_resume', { request_id: r.id });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `${r.email} is pending again and will be deleted when due.` }); reloadAll(); }
  };
  const cancel = async (r: Record<string, any>) => {
    const { error } = await panelPost('users', 'deletion_cancel', { request_id: r.id });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `Request cancelled — ${r.email} keeps their account.` }); reloadAll(); }
  };
  const deleteNow = async (r: Record<string, any>) => {
    setBusy(r.id);
    const { data, error } = await panelPost('users', 'deletion_complete', { request_id: r.id });
    setBusy('');
    if (error || !data?.success) setMsg({ kind: 'error', text: error || data?.error || 'Deletion failed' });
    else {
      setMsg({ kind: 'ok', text: `Deleted ${data.user_email} and all related data; archive ${data.archive_id || '—'} saved first.` });
      afterDeletion();
    }
  };

  /** Fetches the full archive row (data included) and hands it to the browser
   *  as a file — the list endpoint deliberately leaves `data` out. */
  const downloadArchive = async (id: string, email: string) => {
    setBusy(id);
    const { data, error } = await panelGet('users', 'deletion_archive', { id });
    setBusy('');
    if (error || !data) { setMsg({ kind: 'error', text: error || 'Archive not found' }); return; }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `account-archive-${(email || id).replace(/[^\w.@-]+/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Give the click a moment to start the download before the URL goes away.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // --- archives -------------------------------------------------------------
  const [aq, setAq] = useState('');
  const aqd = useDebounced(aq);
  const archives = useAdminGet('users', 'deletion_archives', { q: aqd, limit: 200 }, [aqd, archiveTick]);
  const archiveRows = useMemo(() => (archives.data || []).map((a: Record<string, any>) => ({
    ...a,
    name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || '—',
    size: fmtBytes(a.size_bytes),
    tables_count: a.tables && typeof a.tables === 'object' ? Object.keys(a.tables).length : 0,
  })), [archives.data]);

  const queueRows = useMemo(() => (reqs.data || []).map((r: Record<string, any>) => ({
    ...r,
    name: `${r.first_name || ''} ${r.last_name || ''}`.trim() || '—',
    details: deletionDetailText(r),
  })), [reqs.data]);

  const st = status.data;
  const settings = st?.settings;
  const lastRun = st?.last_run;
  const lastResult = lastRun?.result;
  const dueNow = Number(st?.due_now || 0);
  const isOpen = (r: Record<string, any>) => r.status === 'pending' || r.status === 'on_hold';
  // A failed request is not closed: it can be retried, cancelled or run now.
  // Only `deleting` (cascade in flight) and the terminal statuses get no actions.
  const canAct = (r: Record<string, any>) => isOpen(r) || r.status === 'failed';

  const numInput = 'h-9 w-20 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm';

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <Section title="Automatic deletion" right={<Btn small onClick={status.reload}>Refresh</Btn>}>
        {status.loading && !st ? <Loading /> : status.error ? <Msg kind="error">{status.error}</Msg> : (
          <>
            <div className="flex items-center gap-3 flex-wrap text-sm text-grayscale-1200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={auto}
                  onChange={e => { setAuto(e.target.checked); setDirty(true); }} />
                <span>Delete accounts automatically after</span>
              </label>
              <input type="number" className={numInput} value={days}
                min={DELETION_DAYS.min} max={DELETION_DAYS.max}
                onChange={e => { setDays(e.target.value); setDirty(true); }} />
              <span>days</span>
              <span className="text-grayscale-1100">·</span>
              <span>Check every</span>
              <input type="number" className={numInput} value={every}
                min={DELETION_EVERY.min} max={DELETION_EVERY.max} step={5}
                onChange={e => { setEvery(e.target.value); setDirty(true); }} />
              <span>minutes</span>
              <Btn kind="primary" onClick={saveSettings} disabled={!dirty}>Save</Btn>
              {dueNow > 0
                ? <DangerBtn onConfirm={runNow}>
                    {busy === 'run' ? 'Running…' : `Run now — delete ${dueNow} due account${dueNow === 1 ? '' : 's'}`}
                  </DangerBtn>
                : <Btn disabled title="No pending request has reached its due date">Run now — nothing due</Btn>}
            </div>
            <p className="text-xs text-grayscale-1100 -mt-1">
              Raising the days re-times every pending request; lowering it never deletes before the
              date the user was emailed (shown as "told" on each row). Run now processes whatever is
              due even while the toggle is off.
            </p>

            <KPIRow>
              <KPI label="Pending" value={st?.pending ?? '—'} hint="waiting for their due date" />
              <KPI label="On hold" value={st?.on_hold ?? '—'} hint="paused by an admin" />
              <KPI label="Due now" value={dueNow} hint="past due — next run deletes these" />
            </KPIRow>

            <div className="text-xs text-grayscale-1200 flex flex-col gap-0.5">
              <div>
                Scheduler:{' '}
                {st?.scheduler_running
                  ? <Pill tone="ok">running</Pill>
                  : <Pill tone="bad">not running</Pill>}
                {st?.started_at && <span className="text-grayscale-1100"> since {fmtDate(st.started_at)}</span>}
                {settings && !settings.auto_delete && (
                  <span className="text-amber-700"> — automatic deletion is off, so its checks skip; use Run now or Delete now.</span>
                )}
                {st && !st.scheduler_running && (
                  <span className="text-red-600"> — nothing is deleted automatically (ACCOUNT_DELETION_AUTO=0 or the API did not start it).</span>
                )}
              </div>
              <div>
                Last run:{' '}
                {lastRun?.at ? (
                  <>
                    {fmtDate(lastRun.at)} by <b>{lastRun.by || 'auto'}</b>
                    {lastResult?.skipped
                      ? <span className="text-grayscale-1100"> — skipped ({String(lastResult.skipped)})</span>
                      : <> — checked {lastResult?.checked ?? 0}, deleted {(lastResult?.deleted || []).length},
                          failed {(lastResult?.failed || []).length}</>}
                  </>
                ) : <span className="text-grayscale-1100">none since the API started</span>}
              </div>
              <div>
                Next run: {st?.next_run_estimate
                  ? fmtDate(st.next_run_estimate)
                  : <span className="text-grayscale-1100">—</span>}
                {settings && (
                  <span className="text-grayscale-1100">
                    {' '}(first check {settings.startup_delay_minutes} min after start, then every {settings.interval_minutes} min)
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </Section>

      <Section title="Deletion queue" right={<Btn small onClick={reloadAll}>Refresh</Btn>}>
        <Notice>
          Hold stops a deletion for as long as you need; Cancel keeps the account for good. Every
          deleted account is archived below first. A failed request is never stuck: Retry puts it
          back on the clock, or Cancel closes it.
        </Notice>
        {reqs.loading && !reqs.data ? <Loading /> : reqs.error ? <Msg kind="error">{reqs.error}</Msg> : (
          <DataTable rows={queueRows} csvName="deletion_requests" searchable
            columns={['email', 'name', 'status', 'reason', 'requested_at', 'due_at', 'details']}
            labels={{ due_at: 'Due', requested_at: 'Requested', details: 'Details' }}
            empty="No deletion requests — nobody wants to leave."
            render={{
              status: r => (
                <span className="inline-flex items-center gap-1">
                  {deletionPill(r.status)}
                  {canAct(r) && r.user_exists === false && <Pill tone="warn">user gone</Pill>}
                </span>
              ),
              reason: r => r.reason
                ? <span className="block max-w-[16rem] truncate" title={r.reason}>{r.reason}</span>
                : <span className="text-grayscale-1100">—</span>,
              due_at: r => {
                const label = isOpen(r) ? dueLabel(r.days_left) : '';
                const overdue = label.startsWith('overdue') || label === 'due today';
                return (
                  <div className="flex flex-col leading-tight">
                    <span>{fmtDate(r.due_at)}</span>
                    {label && <span className={`text-[11px] ${overdue ? 'text-red-600' : 'text-grayscale-1100'}`}>{label}</span>}
                    {r.scheduled_deletion_date && (
                      <span className="text-[11px] text-grayscale-1100" title="scheduled_deletion_date — the date the user was told when they asked">
                        told {fmtDate(r.scheduled_deletion_date)}
                      </span>
                    )}
                  </div>
                );
              },
              details: r => {
                if (r.status === 'on_hold') {
                  return (
                    <div className="flex flex-col leading-tight max-w-[18rem]">
                      <span className="truncate" title={r.hold_reason || ''}>{r.hold_reason || <i>no reason given</i>}</span>
                      <span className="text-[11px] text-grayscale-1100">{r.held_by || '?'} · {fmtDate(r.held_at)}</span>
                    </div>
                  );
                }
                if (r.status === 'completed') {
                  return (
                    <div className="flex items-center gap-2">
                      <span>by {r.deleted_by || '—'}</span>
                      {r.archive_id
                        ? <Btn small onClick={() => downloadArchive(r.archive_id, r.email)} disabled={busy === r.archive_id}>
                            {busy === r.archive_id ? 'Fetching…' : 'Archive JSON'}
                          </Btn>
                        : <span className="text-[11px] text-grayscale-1100">no archive</span>}
                    </div>
                  );
                }
                if (r.status === 'failed') {
                  return <span className="text-red-600 block max-w-[18rem] truncate" title={r.last_error || ''}>{r.last_error || 'failed'}</span>;
                }
                return r.details ? <span className="text-grayscale-1100">{r.details}</span> : <span className="text-grayscale-1100">—</span>;
              },
            }}
            actions={r => canAct(r) ? (
              <div className="flex gap-1">
                {r.status === 'pending'
                  ? <Btn small onClick={() => hold(r)}>Hold</Btn>
                  : <Btn small onClick={() => resume(r)}>{r.status === 'failed' ? 'Retry' : 'Resume'}</Btn>}
                <Btn small onClick={() => cancel(r)}>Cancel</Btn>
                {r.user_exists !== false && (
                  <DangerBtn small onConfirm={() => deleteNow(r)}>{busy === r.id ? 'Deleting…' : 'Delete now'}</DangerBtn>
                )}
              </div>
            ) : null} />
        )}
      </Section>

      <Section title="Deleted account archives"
        right={<TextInput value={aq} onChange={setAq} placeholder="Search by email…" />}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          The full snapshot of every account removed by this tool — profile, chats, searches, files,
          billing — taken before the cascade ran, with the user's stated reason. Password hashes and
          tokens are redacted. Download JSON fetches the whole record.
        </p>
        {archives.loading && !archives.data ? <Loading /> : archives.error ? <Msg kind="error">{archives.error}</Msg> : (
          <DataTable rows={archiveRows} csvName="deleted_account_archives"
            columns={['email', 'name', 'reason', 'requested_at', 'deleted_at', 'deleted_by', 'size', 'tables_count']}
            labels={{ tables_count: 'Tables', requested_at: 'Requested', deleted_at: 'Deleted' }}
            empty={aqd ? 'No archive matches that email.' : 'No accounts have been deleted through this tool yet.'}
            render={{
              reason: r => r.reason
                ? <span className="block max-w-[16rem] truncate" title={r.reason}>{r.reason}</span>
                : <span className="text-grayscale-1100">—</span>,
              tables_count: r => (
                <span title={r.tables ? JSON.stringify(r.tables, null, 1) : ''}>{r.tables_count}</span>
              ),
            }}
            actions={r => (
              <Btn small onClick={() => downloadArchive(r.id, r.email)} disabled={busy === r.id}>
                {busy === r.id ? 'Fetching…' : 'Download JSON'}
              </Btn>
            )} />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Contact limits (owner, 2026-10-07) — how many universities' contact details
// an account can open: a default per plan, an account's own limit, and the
// "Request more" queue the bell links to ({group:'users', tab:'Contact limits'}).
// Handlers live in Backend/contact_limits.py (users/contact_limit*). Every open
// is logged, so each plan — and each account's own limit — counts over its own
// period (a calendar day / month / year in IST, the whole trial, or lifetime)
// and changing a period recounts at once. A technology counts once per period,
// so re-opening one is free; team accounts are never limited. The account's
// own limit is also edited in UserDrawer.
//
// Possible abuse (owner, 2026-10-07 — one account opened 96 in an hour, 124 in
// about 3 h): editable "N different technologies within M minutes" rules are
// checked on every open, and an account that breaks one gets an amber or red
// flag (users/contact_flag*) — shown here, in the bell ({group:'users',
// tab:'Contact limits', user}) and in the account's drawer. Suspending stays a
// person's decision (Account access, Backend/account_status.py).
// ---------------------------------------------------------------------------

/** Range contact_limits._limit_value accepts — mirrored so the form can say so first. */
const CL_MAX = 100000;
const CL_TIERS = ['trial', 'paid', 'free'] as const;
type ClTier = typeof CL_TIERS[number];
const CL_TIER_LABEL: Record<string, string> = {
  trial: 'Trial', paid: 'Paid plans', free: 'Free / expired', admin: 'Team account',
};
/** Periods a limit can count over (contact_limits.PERIODS). "Whole trial" only
 *  fits the trial plan — the server refuses it for the others. */
const clPeriodsFor = (tier: string): string[] =>
  tier === 'trial' ? ['trial', 'day', 'month', 'year', 'lifetime'] : ['day', 'month', 'year', 'lifetime'];
const CL_PERIOD_LABEL: Record<string, string> = {
  trial: 'Whole trial', day: 'Per day', month: 'Per month', year: 'Per year', lifetime: 'Lifetime',
};
/** How a limit reads with its period: "50 a calendar month". */
const CL_PERIOD_HINT: Record<string, string> = {
  trial: 'for the whole trial', day: 'a day', month: 'a calendar month', year: 'a calendar year',
  lifetime: 'in total, never resets',
};
/** "12 of 50 {this month}", keyed by the period the server says is in force.
 *  usage.window_label is written for the user ("during your trial"); the admin
 *  reads about someone else. Lifetime only counts opens made on the plan they
 *  are on now. */
const clWindow = (period: any, tier: any): string => {
  switch (period) {
    case 'day': return 'today';
    case 'month': return 'this month';
    case 'year': return 'this year';
    case 'trial': return 'during their trial';
    case 'lifetime':
      return tier === 'free' ? 'on the free plan' : tier === 'paid' ? 'in total on paid plans'
        : tier === 'trial' ? 'in total on trials' : '';
    default: return '';
  }
};
/** "while they're on …" */
const CL_PLAN_WORD: Record<string, string> = {
  trial: 'a trial', paid: 'a paid plan', free: 'the free plan', admin: 'a team account',
};
const CL_STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  open: 'warn', granted: 'ok', declined: 'mute',
};
const CL_FILTERS = ['open', 'all', 'granted', 'declined'];
const CL_FILTER_LABEL: Record<string, string> = {
  open: 'Open', all: 'All', granted: 'Granted', declined: 'Declined',
};
const CL_EMPTY: Record<string, string> = {
  open: 'No open requests — nobody is waiting.',
  all: 'Nobody has asked for more university contacts yet.',
  granted: 'No request has been granted yet.',
  declined: 'No request has been declined.',
};

/** null / undefined = unlimited (the backend's meaning). */
const clLimit = (v: any) =>
  v === null || v === undefined || v === '' ? 'unlimited' : Number(v).toLocaleString();
/** A limit with its period, for sentences: "50 a calendar month", "unlimited". */
const clLimitIn = (v: any, period: any) =>
  v === null || v === undefined || v === '' ? 'unlimited' : `${clLimit(v)} ${CL_PERIOD_HINT[period] || ''}`.trim();

/** '' → null (unlimited); otherwise a whole number in range, or the sentence to show. */
const clParse = (raw: string, what: string): { value: number | null; error: string } => {
  const t = raw.trim();
  if (!t) return { value: null, error: '' };
  const n = Number(t);
  if (!Number.isInteger(n) || n < 0 || n > CL_MAX) {
    return { value: null,
      error: `${what}: give a whole number from 0 to ${CL_MAX.toLocaleString()}, or leave it empty for unlimited.` };
  }
  return { value: n, error: '' };
};

/** The account's name now, else the name typed on the request. */
const clPerson = (r: Record<string, any>) =>
  `${r.first_name || ''} ${r.last_name || ''}`.trim() || String(r.name || '').trim();

const clStatusPill = (r: Record<string, any>) => (
  <span className="inline-flex items-center gap-1">
    <Pill tone={CL_STATUS_TONE[r.status] || 'mute'}>{r.status || '—'}</Pill>
    {r.status === 'granted' && (
      <span className="text-[11px] text-grayscale-1200" title="The limit given">→ {clLimit(r.granted_limit)}</span>
    )}
  </span>
);

/** Grant / Decline / Reopen and every flag action change what the bell shows
 *  (AdminPage listens on document). */
const clAlertsChanged = () => {
  try { document.dispatchEvent(new Event('utech:alerts-changed')); } catch { /* not in a browser */ }
};

// --- Possible-abuse flags -----------------------------------------------------
const CL_FLAG_FILTERS = ['open', 'all'];
const CL_FLAG_FILTER_LABEL: Record<string, string> = { open: 'Open flags', all: 'All flags' };
/** contact_limits._flag_rules' caps — mirrored so the form can say so first. */
const CL_MAX_RULES = 10;
const CL_RULE_MAX_COUNT = 10000;
const CL_RULE_MAX_MINUTES = 60 * 24 * 31;
const CL_FLAG_STATUS_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'mute'> = {
  open: 'warn', dismissed: 'mute', actioned: 'ok',
};
const CL_FLAG_ACTION: Record<string, string> = {
  paused: 'contacts paused', restored: 'contacts restored', suspended: 'account suspended',
};

/** A rule's window as contact_limits._rule_label writes it: 60 min, 24 h, 7 days. */
/** Contact cards a flag's burst actually showed: its tries less the blocked ones. */
const clShown = (f: Record<string, any>) =>
  Math.max(0, Number(f.opens_total || 0) - Number(f.blocked || 0));
const clSpan = (minutes: any): string => {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return '—';
  if (m < 120) return `${m} min`;
  if (m % 60 === 0 && m < 2880) return `${m / 60} h`;
  if (m % 1440 === 0) return `${m / 1440} days`;
  return `${m} min`;
};

/** How long a burst ran, first open to last. */
const clDuration = (from: any, to: any): string => {
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return min % 60 ? `${h} h ${min % 60} min` : `${h} h`;
  return `${Math.round(h / 24)} days`;
};

/** Amber / red flag pill; `text` replaces the word (the Directory says "flagged"). */
const clFlagPill = (severity: any, text?: string) => {
  const red = severity === 'red';
  return (
    <span title={red
      ? 'Red flag — possible contact harvesting. See Users → Contact limits.'
      : 'Amber flag — unusually many university contacts opened in a short time. See Users → Contact limits.'}>
      <Pill tone={red ? 'bad' : 'warn'}>{text || (red ? 'red' : 'amber')}</Pill>
    </span>
  );
};

/** Name · e-mail as a button that opens the account; a suspended one says so. */
const ClPersonBtn: React.FC<{ row: Record<string, any>; onOpen: (userId: string) => void }> =
  ({ row, onOpen }) => {
  const name = clPerson(row);
  return (
    <span className="inline-flex items-center gap-1.5">
      <button type="button" onClick={() => onOpen(String(row.user_id))} title="Open this account"
        className="text-left text-primary-700 hover:underline flex flex-col leading-tight">
        {name && <span className="font-medium">{name}</span>}
        <span className={name ? 'text-[11px]' : ''}>{row.email || row.user_id}</span>
      </button>
      {row.is_active === false && <Pill tone="bad">suspended</Pill>}
    </span>
  );
};

type ClDraft = {
  trial: string; paid: string; free: string; periods: Record<ClTier, string>;
  notify: boolean; recipients: string;
};
const clNum = (v: any) => (v === null || v === undefined ? '' : String(v));
const clDraftFrom = (s: Record<string, any>): ClDraft => ({
  trial: clNum(s.trial), paid: clNum(s.paid), free: clNum(s.free),
  // The server always sends periods; the fallbacks are its own defaults.
  periods: {
    trial: s.periods?.trial || 'trial', paid: s.periods?.paid || 'month', free: s.periods?.free || 'lifetime',
  },
  notify: s.notify_enabled !== false,
  recipients: (Array.isArray(s.recipients) ? s.recipients : []).join(', '),
});

const ContactLimits: React.FC<{ focus?: Focus | null }> = ({ focus }) => {
  const [filter, setFilter] = useState('open');
  const ov = useAdminGet('users', 'contact_limits', filter === 'all' ? {} : { status: filter }, [filter]);
  const [msg, setMsg] = useState<Flash>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ row: Record<string, any>; mode: 'grant' | 'decline' } | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState('');
  // Bumped after anything that can change a flag or an opener's line (a flag
  // action, a scan, an edit in the drawer), so those two tables re-read.
  const [tick, setTick] = useState(0);
  const refresh = () => { ov.reload(); setTick(t => t + 1); };

  // The bell's possible-abuse items link here with the account: open it.
  useEffect(() => {
    if (focus?.user) setSelected(focus.user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n]);

  // Defaults form. `dirty` keeps a half-typed edit from being overwritten by
  // the refetch that follows every queue action or filter change; `seededFrom`
  // makes a save's own answer stick until the refetch brings the same values
  // (clearing dirty must not copy the pre-save settings back in).
  const settings = ov.data?.settings;
  const [draft, setDraft] = useState<ClDraft>({
    trial: '', paid: '', free: '', periods: { trial: 'trial', paid: 'month', free: 'lifetime' },
    notify: true, recipients: '',
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const seededFrom = useRef<any>(null);
  useEffect(() => {
    if (!settings || dirty || seededFrom.current === settings) return;
    seededFrom.current = settings;
    setDraft(clDraftFrom(settings));
  }, [settings, dirty]);
  const edit = (patch: Partial<ClDraft>) => { setDraft(d => ({ ...d, ...patch })); setDirty(true); };
  const editLimit = (t: ClTier, v: string) => { setDraft(d => ({ ...d, [t]: v })); setDirty(true); };
  const editPeriod = (t: ClTier, v: string) => {
    setDraft(d => ({ ...d, periods: { ...d.periods, [t]: v } }));
    setDirty(true);
  };
  const discard = () => { if (settings) setDraft(clDraftFrom(settings)); seededFrom.current = settings; setDirty(false); };

  const save = async () => {
    const parsed = CL_TIERS.map(t => clParse(draft[t], CL_TIER_LABEL[t]));
    const bad = parsed.find(p => p.error);
    if (bad) { setMsg({ kind: 'error', text: bad.error }); return; }
    setSaving(true);
    const { data, error } = await panelPost('users', 'contact_limits_set', {
      trial: parsed[0].value, paid: parsed[1].value, free: parsed[2].value, periods: draft.periods,
      notify_enabled: draft.notify, recipients: draft.recipients,
    });
    setSaving(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const s = data?.settings;
    if (s) setDraft(clDraftFrom(s));
    seededFrom.current = settings;
    setDirty(false);
    setMsg({ kind: 'ok', text: s
      ? `Saved — ${CL_TIERS.map(t => `${CL_TIER_LABEL[t].toLowerCase()} ${clLimitIn(s[t], s.periods?.[t])}`).join(', ')}.`
        + (s.notify_enabled ? ` New requests are e-mailed to ${(s.effective_recipients || []).join(', ')}.`
          : ' New requests are not e-mailed — they show here and on the bell.')
      : 'Saved.' });
    ov.reload();
  };

  const reopen = async (r: Record<string, any>) => {
    setBusy(String(r.id));
    // user_id / email ride along only so the audit row names the account.
    const { error } = await panelPost('users', 'contact_limit_request_status',
      { id: r.id, status: 'open', user_id: r.user_id, email: r.email });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: `Reopened. Any limit given earlier stays on the account — change it from the account if needed.` });
    ov.reload();
    clAlertsChanged();
  };

  const decided = (text: string) => {
    setDeciding(null);
    setMsg({ kind: 'ok', text });
    ov.reload();
    clAlertsChanged();
  };

  const rows = useMemo(() => (ov.data?.requests || []).map((r: Record<string, any>) => ({
    ...r,
    person: [clPerson(r), r.email].filter(Boolean).join(' · '),
    plan: CL_TIER_LABEL[r.tier] || r.tier || '—',
    used_then: `${Number(r.used || 0).toLocaleString()} / ${clLimit(r.view_limit)}`,
    asked_for: r.extra_requested ? `${Number(r.extra_requested).toLocaleString()} more` : 'not said',
    emailed: r.email_sent ? 'sent' : `not sent${r.email_error ? ` — ${r.email_error}` : ''}`,
  })), [ov.data]);

  const k = ov.data?.kpis || {};
  const toggle = (s: string) => setFilter(f => (f === s ? 'all' : s));
  const numInput = 'h-9 w-28 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300';
  const selCls = 'h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300';
  const tz = String(settings?.period_timezone || 'India time (IST)');

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <Section title="Default limits" right={<Btn small onClick={refresh}>Refresh</Btn>}>
        {ov.loading && !settings ? <Loading /> : !settings ? (ov.error ? <Msg kind="error">{ov.error}</Msg> : null) : (
          <>
            <p className="text-xs text-grayscale-1100 -mt-1">
              How many universities' contact details each account can open, and over what period.
              Leave a box empty for unlimited. Per day, month and year follow the calendar in {tz}: the
              count starts again at midnight, on the 1st of the month, or on 1 January. A trial counts
              over the whole trial unless you pick otherwise, and a new trial starts a fresh count.
              Lifetime never resets. Changing a period recounts straight away from what people
              have already opened. Each technology counts once per period — opening it again is free,
              and one with no contact details does not count. Free or expired accounts can only open
              contacts at all when the Free-tier lock in Billing allows it
              {settings.free_locked ? ' (it does not right now)' : ''}; this number is how many once it
              does. Team accounts are never limited. To give one person a different limit, open their
              account (click a name below) or grant their request.
            </p>
            <div className="flex items-end gap-5 flex-wrap">
              {CL_TIERS.map(t => (
                <div key={t} className="flex flex-col gap-1 text-xs text-grayscale-1100">
                  <span>{CL_TIER_LABEL[t]}</span>
                  <div className="flex items-center gap-1.5">
                    <input type="number" min={0} max={CL_MAX} step={1} value={draft[t]} placeholder="unlimited"
                      aria-label={`${CL_TIER_LABEL[t]}: limit`}
                      onChange={e => editLimit(t, e.target.value)} className={numInput} />
                    <select value={draft.periods[t]} onChange={e => editPeriod(t, e.target.value)}
                      aria-label={`${CL_TIER_LABEL[t]}: counted over`} className={selCls}>
                      {clPeriodsFor(t).map(p => <option key={p} value={p}>{CL_PERIOD_LABEL[p]}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <Check checked={draft.notify} onChange={v => edit({ notify: v })}>
                E-mail the team when someone asks for more
              </Check>
              <Labeled label="Send to" hint="comma-separated · leave empty for info@universitytech.io">
                <input value={draft.recipients} onChange={e => edit({ recipients: e.target.value })}
                  placeholder="info@universitytech.io" spellCheck={false} className={inputCls} />
              </Labeled>
              <div className="text-[11px] text-grayscale-1100">
                {settings.notify_enabled
                  ? <>Right now requests are e-mailed to {(settings.effective_recipients || []).join(', ') || '—'}, with
                      the person's address as Reply-To.</>
                  : <>Right now requests are not e-mailed — they show in the table below and on the bell.</>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Btn kind="primary" onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</Btn>
              {dirty && !saving && <Btn onClick={discard}>Discard changes</Btn>}
            </div>
          </>
        )}
      </Section>

      {settings && (
        <ClFlagRules settings={settings}
          onSaved={text => { setMsg({ kind: 'ok', text }); ov.reload(); }} />
      )}

      <KPIRow>
        <KPI label="Open requests" value={k.open_requests ?? '—'} hint="waiting for an answer"
          onClick={() => toggle('open')} active={filter === 'open'} />
        <KPI label="Granted" value={k.granted ?? '—'} onClick={() => toggle('granted')} active={filter === 'granted'} />
        <KPI label="Declined" value={k.declined ?? '—'} onClick={() => toggle('declined')} active={filter === 'declined'} />
        <KPI label="Open flags" value={k.open_flags ?? '—'} hint="possible abuse, below" />
        <KPI label="Contact views" value={k.views_30d ?? '—'} hint="last 30 days" />
        <KPI label="Viewers" value={k.viewers_30d ?? '—'} hint="people, last 30 days" />
        <KPI label="Blocked" value={k.blocked_30d ?? '—'} hint="over their limit, last 30 days" />
        <KPI label="Own limits" value={k.account_limits ?? '—'} hint="accounts with their own limit" />
      </KPIRow>

      <ClFlags reloadKey={tick} onOpen={setSelected} onChanged={refresh} />

      <Section title="Requests for more"
        right={
          <div className="flex items-center gap-2">
            <Select value={filter} onChange={setFilter} options={CL_FILTERS} labels={CL_FILTER_LABEL} />
            <Btn small onClick={ov.reload}>Refresh</Btn>
          </div>
        }>
        {/* With no data at all the error shows once, in the card above; an
            empty table here would read as "nobody is waiting". */}
        {ov.error && settings && <Msg kind="error">{ov.error}</Msg>}
        {ov.loading && !ov.data ? <Loading /> : !ov.data ? null : (
          <DataTable rows={rows} tableId="contact_limit_requests" csvName="contact_limit_requests"
            columns={['created_at', 'person', 'plan', 'used_then', 'asked_for', 'reason', 'status', 'emailed']}
            optionalColumns={['email', 'admin_note', 'handled_by', 'handled_at', 'granted_limit', 'id']}
            labels={{
              created_at: 'Requested', person: 'Person', plan: 'Plan', used_then: 'Used / limit when asked',
              asked_for: 'Asked for', reason: 'Why', status: 'Status', emailed: 'E-mail sent?',
              admin_note: 'Note', handled_by: 'Handled by', handled_at: 'Handled', granted_limit: 'Limit given',
              id: 'Request #',
            }}
            empty={CL_EMPTY[filter] || CL_EMPTY.all}
            render={{
              person: r => r.user_id ? (
                <button type="button" onClick={() => setSelected(String(r.user_id))}
                  title="Open this account" className="text-left text-primary-700 hover:underline flex flex-col leading-tight">
                  {clPerson(r) && <span className="font-medium">{clPerson(r)}</span>}
                  <span className={clPerson(r) ? 'text-[11px]' : ''}>{r.email || r.user_id}</span>
                </button>
              ) : <span>{r.person || '—'}</span>,
              reason: r => {
                if (!r.reason) return <span className="text-grayscale-1100">—</span>;
                const open = !!expanded[r.id];
                return (
                  <button type="button" onClick={() => setExpanded(e => ({ ...e, [r.id]: !e[r.id] }))}
                    title={open ? 'Click to shorten' : `${r.reason}\n\n(click to show it all)`}
                    className={`block text-left max-w-[18rem] ${open ? 'whitespace-pre-wrap break-words' : 'truncate'}`}>
                    {r.reason}
                  </button>
                );
              },
              status: r => (
                <div className="flex flex-col leading-tight max-w-[18rem]">
                  {clStatusPill(r)}
                  {r.status !== 'open' && (r.handled_by || r.handled_at) && (
                    <span className="text-[11px] text-grayscale-1100">{r.handled_by || '?'} · {fmtDate(r.handled_at)}</span>
                  )}
                  {r.admin_note && (
                    <span className="text-[11px] text-grayscale-1200 truncate" title={r.admin_note}>{r.admin_note}</span>
                  )}
                </div>
              ),
              emailed: r => r.email_sent ? <Pill tone="ok">sent</Pill> : (
                <span className="inline-flex items-center gap-1 max-w-[16rem]">
                  <Pill tone={/notifications off/i.test(String(r.email_error || '')) ? 'mute' : 'bad'}>not sent</Pill>
                  {r.email_error && (
                    <span className="text-[11px] text-grayscale-1100 truncate" title={r.email_error}>{r.email_error}</span>
                  )}
                </span>
              ),
            }}
            actions={r => r.status === 'open' ? (
              <div className="flex gap-1">
                <Btn small kind="primary" onClick={() => setDeciding({ row: r, mode: 'grant' })}>Grant…</Btn>
                <Btn small onClick={() => setDeciding({ row: r, mode: 'decline' })}>Decline…</Btn>
              </div>
            ) : (
              <Btn small disabled={busy === String(r.id)} onClick={() => reopen(r)}
                title="Put it back in the open queue (the limit given stays on the account)">
                {busy === String(r.id) ? 'Reopening…' : 'Reopen'}
              </Btn>
            )} />
        )}
      </Section>

      <ClTopOpeners reloadKey={tick} onOpen={setSelected} />

      {deciding && (
        <ClDecisionModal row={deciding.row} mode={deciding.mode}
          period={settings?.periods?.[deciding.row.tier]}
          onClose={() => setDeciding(null)} onDone={decided} />
      )}
      {selected && <UserDrawer userId={selected}
        onClose={() => { setSelected(null); refresh(); }}
        onChanged={refresh} />}
    </div>
  );
};

type ClRuleDraft = { count: string; minutes: string; severity: string };
const clRulesFrom = (s: Record<string, any>): ClRuleDraft[] =>
  (Array.isArray(s.flag_rules) ? s.flag_rules : []).map((r: any) => ({
    count: clNum(r.count), minutes: clNum(r.minutes), severity: r.severity === 'red' ? 'red' : 'amber',
  }));

/**
 * Abuse detection — the flag rules and what happens when an account turns red.
 * Saved on its own (contact_limits_set keeps whatever it is not sent), so a
 * half-edited default limit in the card above is never saved by accident.
 */
const ClFlagRules: React.FC<{ settings: Record<string, any>; onSaved: (text: string) => void }> =
  ({ settings, onSaved }) => {
  const [rules, setRules] = useState<ClRuleDraft[]>(() => clRulesFrom(settings));
  const [emailOnRed, setEmailOnRed] = useState<boolean>(!!settings.flag_email_on_red);
  const [pauseOnRed, setPauseOnRed] = useState<boolean>(!!settings.flag_pause_on_red);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // Same seeding rule as the defaults card above.
  const seededFrom = useRef<any>(settings);
  const seed = (s: Record<string, any>) => {
    setRules(clRulesFrom(s));
    setEmailOnRed(!!s.flag_email_on_red);
    setPauseOnRed(!!s.flag_pause_on_red);
  };
  useEffect(() => {
    if (dirty || seededFrom.current === settings) return;
    seededFrom.current = settings;
    seed(settings);
  }, [settings, dirty]);

  const touch = () => { setDirty(true); setErr(''); };
  const setRule = (i: number, patch: Partial<ClRuleDraft>) => {
    setRules(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    touch();
  };
  const addRule = () => {
    setRules(rs => (rs.length >= CL_MAX_RULES ? rs : [...rs, { count: '30', minutes: '60', severity: 'amber' }]));
    touch();
  };
  const removeRule = (i: number) => { setRules(rs => rs.filter((_, j) => j !== i)); touch(); };
  const discard = () => { seed(settings); seededFrom.current = settings; setDirty(false); setErr(''); };

  const save = async () => {
    const out: { count: number; minutes: number; severity: string }[] = [];
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      const n = Number(r.count);
      const m = Number(r.minutes);
      if (!r.count.trim() || !Number.isInteger(n) || n < 2 || n > CL_RULE_MAX_COUNT) {
        setErr(`Rule ${i + 1}: the number of technologies must be a whole number from 2 to ${CL_RULE_MAX_COUNT.toLocaleString()}.`);
        return;
      }
      if (!r.minutes.trim() || !Number.isInteger(m) || m < 1 || m > CL_RULE_MAX_MINUTES) {
        setErr(`Rule ${i + 1}: minutes must be a whole number from 1 to ${CL_RULE_MAX_MINUTES.toLocaleString()} (31 days).`);
        return;
      }
      out.push({ count: n, minutes: m, severity: r.severity === 'red' ? 'red' : 'amber' });
    }
    setSaving(true);
    setErr('');
    const { data, error } = await panelPost('users', 'contact_limits_set', {
      flag_rules: out, flag_email_on_red: emailOnRed, flag_pause_on_red: pauseOnRed,
    });
    setSaving(false);
    if (error) { setErr(error); return; }
    const s = data?.settings;
    if (s) seed(s);
    seededFrom.current = settings;
    setDirty(false);
    const n = (s?.flag_rules || out).length;
    const after = [
      (s?.flag_email_on_red ?? emailOnRed) ? 'the team is e-mailed' : '',
      (s?.flag_pause_on_red ?? pauseOnRed) ? 'their contacts are paused' : '',
    ].filter(Boolean);
    onSaved(n
      ? `Saved — ${n} flag rule${n === 1 ? '' : 's'}. When an account turns red, `
        + (after.length ? `${after.join(' and ')}.` : 'it is only flagged (no e-mail, no pause).')
      : 'Saved — no flag rules, so nobody will be flagged.');
  };

  const smallNum = 'h-8 w-20 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300';
  const to = (settings.effective_recipients || []).join(', ');

  return (
    <Section title="Abuse detection">
      <p className="text-xs text-grayscale-1100 -mt-1">
        Flags an account that opens the contact details of many different technologies in a short
        time — someone copying the contact list rather than using it. The rules are checked every time
        someone opens a contact card, and attempts their limit blocked count too. A flagged account
        shows under Possible abuse below, in the bell and on the account. Team accounts are never
        flagged. Suspending an account is never automatic.
      </p>
      <div className="flex flex-col gap-2">
        {rules.map((r, i) => (
          <div key={i} className="flex items-center gap-2 flex-wrap text-sm text-grayscale-1300">
            <span>Flag when</span>
            <input type="number" min={2} max={CL_RULE_MAX_COUNT} step={1} value={r.count}
              aria-label={`Rule ${i + 1}: how many different technologies`}
              onChange={e => setRule(i, { count: e.target.value })} className={smallNum} />
            <span>different technologies are opened within</span>
            <input type="number" min={1} max={CL_RULE_MAX_MINUTES} step={1} value={r.minutes}
              aria-label={`Rule ${i + 1}: within how many minutes`}
              onChange={e => setRule(i, { minutes: e.target.value })} className={smallNum} />
            <span>
              minutes
              {Number(r.minutes) >= 120 && <span className="text-grayscale-1100"> ({clSpan(r.minutes)})</span>}
            </span>
            <span aria-hidden className="text-grayscale-1100">→</span>
            <select value={r.severity} onChange={e => setRule(i, { severity: e.target.value })}
              aria-label={`Rule ${i + 1}: flag colour`}
              className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300">
              <option value="amber">amber</option>
              <option value="red">red</option>
            </select>
            <Btn small onClick={() => removeRule(i)} title="Remove this rule">Remove</Btn>
          </div>
        ))}
        {!rules.length && <div className="text-xs text-amber-700">No rules — nobody will be flagged.</div>}
        <div className="flex items-center gap-2">
          <Btn small onClick={addRule} disabled={rules.length >= CL_MAX_RULES}
            title={rules.length >= CL_MAX_RULES ? `At most ${CL_MAX_RULES} rules` : 'Add another rule'}>
            Add rule
          </Btn>
          {rules.length >= CL_MAX_RULES && (
            <span className="text-[11px] text-grayscale-1100">At most {CL_MAX_RULES} rules.</span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Check checked={emailOnRed} onChange={v => { setEmailOnRed(v); touch(); }}>
          E-mail the team when an account turns red
          {to && <span className="text-grayscale-1100"> — sent to {to}</span>}
        </Check>
        <Check checked={pauseOnRed} onChange={v => { setPauseOnRed(v); touch(); }}>
          Pause their contacts automatically when red
          <span className="text-grayscale-1100">
            {' '}— holds them at what they have opened so far; they see "limit reached" and can still ask
            for more. Restore it from the flag below.
          </span>
        </Check>
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      <div className="flex items-center gap-2">
        <Btn kind="primary" onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</Btn>
        {dirty && !saving && <Btn onClick={discard}>Discard changes</Btn>}
      </div>
    </Section>
  );
};

/**
 * Possible abuse — accounts whose opens broke a flag rule, red first. Pause
 * holds their contacts where they are (an own limit, undone by Restore);
 * Dismiss closes the flag; Suspend account goes through Account access and
 * then marks the flag handled.
 */
const ClFlags: React.FC<{ reloadKey: number; onOpen: (userId: string) => void; onChanged: () => void }> =
  ({ reloadKey, onOpen, onChanged }) => {
  const [filter, setFilter] = useState('open');
  const flags = useAdminGet('users', 'contact_flags', { status: filter }, [filter, reloadKey]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<Flash>(null);
  const [dismissing, setDismissing] = useState<Record<string, any> | null>(null);
  const [suspending, setSuspending] = useState<Record<string, any> | null>(null);
  const [scanning, setScanning] = useState(false);

  const who = (r: Record<string, any>) => clPerson(r) || r.email || 'this account';

  /** One contact_flag_status call; '' on success, else the error sentence. */
  const post = async (r: Record<string, any>, body: Record<string, any>): Promise<string> => {
    setBusy(String(r.id));
    // user_id / email ride along only so the audit row names the account.
    const { error } = await panelPost('users', 'contact_flag_status',
      { id: r.id, ...body, user_id: r.user_id, email: r.email });
    setBusy('');
    if (!error) { onChanged(); clAlertsChanged(); }
    return error;
  };
  const act = async (r: Record<string, any>, body: Record<string, any>, ok: string) => {
    setMsg(null);
    const error = await post(r, body);
    setMsg(error ? { kind: 'error', text: error } : { kind: 'ok', text: ok });
  };

  const pause = (r: Record<string, any>) => act(r, { pause: true },
    `Paused — ${who(r)} cannot open any new university contacts (ones they already opened still work). `
    + 'They see "limit reached" and can ask for more. Restore puts back the limit they had.');
  const restore = (r: Record<string, any>) => act(r, { restore: true },
    `Restored — ${who(r)}'s limit is back to what it was before the pause.`);
  const reopen = (r: Record<string, any>) => act(r, { status: 'open' }, `Reopened the flag on ${who(r)}.`);
  const closeSuspended = (r: Record<string, any>) => act(r, { status: 'actioned', action: 'suspended' },
    `Closed — ${who(r)} is already suspended.`);

  const dismiss = async (r: Record<string, any>, note: string): Promise<string> => {
    const error = await post(r, { status: 'dismissed', note });
    if (error) return error;
    setDismissing(null);
    setMsg({ kind: 'ok', text: `Dismissed. Opens up to now will not flag ${who(r)} again; a new burst will.`
      + (r.action === 'paused' ? ' Their contacts are still paused — Restore puts their limit back.' : '') });
    return '';
  };

  // The account is suspended by the time this runs; only the flag is left.
  const suspended = async (r: Record<string, any>, status?: any) => {
    setSuspending(null);
    setMsg(null);
    const error = await post(r, { status: 'actioned', action: 'suspended' });
    // What the suspension did (sessions, Razorpay, notice) — a failed stop at
    // Razorpay must be on screen, not only in the account's history.
    const done = status ? describeSuspension(status) : null;
    setMsg(error
      ? { kind: 'error', text: `${who(r)} is suspended, but the flag could not be closed: ${error}` }
      : { kind: done?.kind === 'error' ? 'error' : 'ok',
          text: `${who(r)}: ${done ? done.text : 'Suspended.'} Flag closed.` });
    if (error) onChanged();
  };

  const scan = async () => {
    setScanning(true);
    setMsg(null);
    const { data, error } = await panelPost('users', 'contact_flags_scan', { days: 30 });
    setScanning(false);
    if (error) { setMsg({ kind: 'error', text: `Scan failed: ${error}` }); return; }
    const f: any[] = data?.flagged || [];
    const names = f.slice(0, 5)
      .map(x => `${x.email || x.user_id} (${x.severity}, ${Number(x.peak || 0).toLocaleString()} different)`).join(', ');
    setMsg({ kind: 'ok', text:
      `Checked ${Number(data?.accounts_checked || 0).toLocaleString()} account(s) that opened contacts in the last `
      + `${data?.days ?? 30} days. `
      + (f.length ? `${f.length} broke a rule and ${f.length === 1 ? 'is' : 'are'} flagged: ${names}${f.length > 5 ? '…' : ''}.`
        : 'Nobody broke a rule.') });
    onChanged();
    clAlertsChanged();
  };

  const rows = useMemo(() => (flags.data?.rows || []).map((r: Record<string, any>) => {
    const u = r.usage;
    return {
      ...r,
      person: [clPerson(r), r.email].filter(Boolean).join(' · ') || r.user_id,
      peak: `${r.peak_distinct} different in ${clSpan(r.window_minutes)}`,
      // opens_total counts every try in the window, the blocked ones included.
      activity: `${clShown(r).toLocaleString()} shown · ${Number(r.blocked || 0).toLocaleString()} blocked`,
      span: `${fmtDate(r.first_at)} → ${fmtDate(r.last_at)}`,
      plan: u ? `${CL_TIER_LABEL[u.tier] || u.tier} · ${Number(u.used || 0).toLocaleString()} / ${clLimit(u.limit)}`
        + ` ${clWindow(u.period, u.tier)}`.trimEnd() : '',
      state: [r.status, CL_FLAG_ACTION[r.action] || r.action, r.handled_by].filter(Boolean).join(' · '),
    };
  }), [flags.data]);
  const openCount = rows.filter((r: Record<string, any>) => r.status === 'open').length;

  return (
    <Section title={`Possible abuse${openCount ? ` (${openCount} open)` : ''}`}
      right={
        <div className="flex items-center gap-2">
          <Select value={filter} onChange={setFilter} options={CL_FLAG_FILTERS} labels={CL_FLAG_FILTER_LABEL} />
          <Btn small onClick={scan} disabled={scanning}
            title="Replay the rules over the last 30 days of opens, including the older records from before every open was logged">
            {scanning ? 'Scanning…' : 'Scan history'}
          </Btn>
          <Btn small onClick={flags.reload}>Refresh</Btn>
        </div>
      }>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Accounts that broke a rule under Abuse detection, red first. Pause contacts holds them at what
        they have opened (Restore undoes it). Dismiss closes the flag — only new opens can flag them
        again. Suspend account stops them signing in. Scan history checks the last 30 days, including
        opens from before they were logged.
      </p>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {flags.error && <Msg kind="error">{flags.error}</Msg>}
      {flags.loading && !flags.data ? <Loading /> : !flags.data ? null : (
        <DataTable rows={rows} tableId="contact_flags" csvName="contact_flags"
          columns={['person', 'severity', 'rule', 'peak', 'activity', 'span', 'source', 'plan', 'state']}
          optionalColumns={['created_at', 'updated_at', 'email', 'note', 'handled_by', 'handled_at', 'emailed', 'id']}
          labels={{
            person: 'Person', severity: 'Level', rule: 'Rule broken', peak: 'Peak', activity: 'Opens · blocked',
            span: 'First → last open', source: 'Found', plan: 'Plan · used / limit', state: 'Status',
            created_at: 'Flagged', updated_at: 'Last raised', note: 'Note', handled_by: 'Handled by',
            handled_at: 'Handled', emailed: 'Team e-mailed?', id: 'Flag #',
          }}
          empty={filter === 'open'
            ? 'No open flags — nobody has opened contacts unusually fast.'
            : 'No account has been flagged yet.'}
          render={{
            person: r => <ClPersonBtn row={r} onOpen={onOpen} />,
            severity: r => clFlagPill(r.severity),
            peak: r => <span><b>{Number(r.peak_distinct || 0).toLocaleString()}</b> different in {clSpan(r.window_minutes)}</span>,
            activity: r => (
              <span>
                {clShown(r).toLocaleString()} shown ·{' '}
                <span className={Number(r.blocked) ? 'text-red-700' : 'text-grayscale-1100'}>
                  {Number(r.blocked || 0).toLocaleString()} blocked
                </span>
              </span>
            ),
            span: r => (
              <span className="flex flex-col leading-tight">
                <span>{fmtDate(r.first_at)} → {fmtDate(r.last_at)}</span>
                {clDuration(r.first_at, r.last_at) && (
                  <span className="text-[11px] text-grayscale-1100">over {clDuration(r.first_at, r.last_at)}</span>
                )}
              </span>
            ),
            source: r => r.source === 'history'
              ? <span title="Found by Scan history"><Pill tone="mute">history scan</Pill></span>
              : <span title="Caught as it happened"><Pill tone="mute">live</Pill></span>,
            plan: r => {
              const u = r.usage;
              if (!u) return <span className="text-grayscale-1100">—</span>;
              const full = u.limit !== null && u.limit !== undefined && Number(u.used) >= Number(u.limit);
              return (
                <span className="flex flex-col leading-tight">
                  <span>{CL_TIER_LABEL[u.tier] || u.tier}</span>
                  <span className={`text-[11px] ${full ? 'text-red-700' : 'text-grayscale-1100'}`}>
                    {Number(u.used || 0).toLocaleString()} / {clLimit(u.limit)} {clWindow(u.period, u.tier)}
                  </span>
                </span>
              );
            },
            state: r => (
              <div className="flex flex-col leading-tight max-w-[18rem]">
                <span className="inline-flex items-center gap-1">
                  <Pill tone={CL_FLAG_STATUS_TONE[r.status] || 'mute'}>{r.status || '—'}</Pill>
                  {r.action && (
                    <span className="text-[11px] text-grayscale-1200">{CL_FLAG_ACTION[r.action] || r.action}</span>
                  )}
                </span>
                {r.status !== 'open' && (r.handled_by || r.handled_at) && (
                  <span className="text-[11px] text-grayscale-1100">{r.handled_by || '?'} · {fmtDate(r.handled_at)}</span>
                )}
                {r.note && <span className="text-[11px] text-grayscale-1200 truncate" title={r.note}>{r.note}</span>}
              </div>
            ),
            emailed: r => r.emailed ? <Pill tone="ok">sent</Pill> : <span className="text-grayscale-1100">—</span>,
          }}
          actions={r => {
            const b = busy === String(r.id);
            const isOpen = r.status === 'open';
            const gone = r.is_active === false;
            return (
              <div className="flex gap-1 items-center">
                <Btn small onClick={() => onOpen(String(r.user_id))}>Open account</Btn>
                {isOpen && !gone && r.action !== 'paused' && (
                  <Btn small disabled={b} onClick={() => pause(r)}
                    title="Hold them at what they have opened so far. They see 'limit reached' and can ask for more. Undo with Restore.">
                    Pause contacts
                  </Btn>
                )}
                {r.action === 'paused' && (
                  <Btn small disabled={b} onClick={() => restore(r)}
                    title="Put back the limit they had before the pause">
                    Restore
                  </Btn>
                )}
                {isOpen && <Btn small disabled={b} onClick={() => setDismissing(r)}>Dismiss…</Btn>}
                {isOpen && !gone && (
                  <Btn small kind="danger" disabled={b} onClick={() => setSuspending(r)}
                    title="Stop this account signing in (asks for a reason first)">
                    Suspend account…
                  </Btn>
                )}
                {isOpen && gone && (
                  <Btn small disabled={b} onClick={() => closeSuspended(r)}
                    title="The account is already suspended — mark this flag as handled">
                    Close as suspended
                  </Btn>
                )}
                {!isOpen && (
                  <Btn small disabled={b} onClick={() => reopen(r)} title="Put this flag back in the open list">
                    Reopen
                  </Btn>
                )}
              </div>
            );
          }} />
      )}
      {dismissing && (
        <ClDismissFlagModal row={dismissing} onClose={() => setDismissing(null)}
          onConfirm={note => dismiss(dismissing, note)} />
      )}
      {suspending && (
        <SuspendAccountModal userId={String(suspending.user_id)} email={suspending.email || undefined}
          onClose={() => setSuspending(null)} onDone={(st: any) => suspended(suspending, st)} />
      )}
    </Section>
  );
};

/** Dismiss one flag, with an optional note on why it is fine. */
const ClDismissFlagModal: React.FC<{
  row: Record<string, any>; onClose: () => void; onConfirm: (note: string) => Promise<string>;
}> = ({ row, onClose, onConfirm }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const who = clPerson(row) || row.email || 'This account';
  const submit = async () => {
    setBusy(true);
    setErr('');
    const e = await onConfirm(note.trim());
    setBusy(false);
    if (e) setErr(e);
  };
  return (
    <Modal title="Dismiss this flag" onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind="primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Dismiss flag'}</Btn>
        </>
      }>
      <div className="flex flex-col gap-3">
        <div className="text-sm text-grayscale-1300">
          <b>{who}</b>{row.email && clPerson(row) ? <span className="text-grayscale-1100"> · {row.email}</span> : ''}
          {' '}opened {Number(row.peak_distinct || 0).toLocaleString()} different technologies' contacts
          within {clSpan(row.window_minutes)} ({row.rule}).
        </div>
        <div className="text-[11px] text-grayscale-1100">
          The flag closes and leaves the bell. Opens up to now will not flag them again; a new burst will.
          {row.action === 'paused' ? ' Their contacts stay paused — use Restore to put their limit back.' : ''}
        </div>
        <Labeled label="Note" hint="optional · why this is fine, kept with the flag">
          <input value={note} maxLength={1000} autoFocus onChange={e => setNote(e.target.value)} className={inputCls} />
        </Labeled>
        {err && <Msg kind="error">{err}</Msg>}
      </div>
    </Modal>
  );
};

const CL_TOP_WINDOWS: Array<{ hours: number; label: string; phrase: string }> = [
  { hours: 24, label: 'Last 24 h', phrase: 'in the last 24 hours' },
  { hours: 168, label: 'Last 7 days', phrase: 'in the last 7 days' },
  { hours: 720, label: 'Last 30 days', phrase: 'in the last 30 days' },
];

/** Opens before per-open logging began come from the Contact University lead
 *  records: shown as history, never counted toward a limit. */
const clEarlierNote = (logStarted?: string) => logStarted
  ? ` Opens before ${fmtDate(logStarted)}, when every open started being logged, come from the Contact University`
    + ' lead records; they are history only and never count toward a limit.'
  : '';

/** Top openers — who opened the most different technologies' contacts lately:
 *  what normal looks like, before a rule is tightened. */
const ClTopOpeners: React.FC<{ reloadKey: number; onOpen: (userId: string) => void }> = ({ reloadKey, onOpen }) => {
  const [hours, setHours] = useState(24);
  const top = useAdminGet('users', 'contact_top', { hours }, [hours, reloadKey]);
  const rows = useMemo(() => (top.data?.rows || []).map((r: Record<string, any>, i: number) => ({
    ...r,
    rank: i + 1,
    person: [clPerson(r), r.email].filter(Boolean).join(' · ') || r.user_id,
    flag: r.open_flag || '',
  })), [top.data]);
  const phrase = CL_TOP_WINDOWS.find(w => w.hours === hours)?.phrase || '';
  return (
    <Section title="Top openers"
      right={
        <div className="flex items-center gap-1">
          {CL_TOP_WINDOWS.map(w => (
            <Btn key={w.hours} small kind={hours === w.hours ? 'primary' : 'subtle'} onClick={() => setHours(w.hours)}>
              {w.label}
            </Btn>
          ))}
          <Btn small onClick={top.reload}>Refresh</Btn>
        </div>
      }>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Who opened the most different technologies' contacts {phrase}, most first — useful for seeing
        what normal looks like before changing a rule. Team accounts are included.
        {clEarlierNote(top.data?.log_started)}
      </p>
      {top.error && <Msg kind="error">{top.error}</Msg>}
      {top.loading && !top.data ? <Loading /> : !top.data ? null : (
        <DataTable rows={rows} tableId="contact_top" csvName={`contact_top_${hours}h`}
          columns={['rank', 'person', 'techs', 'opens', 'blocked', 'last_at', 'flag']}
          optionalColumns={['first_at', 'earlier_opens', 'email', 'user_id']}
          labels={{
            rank: '#', person: 'Person', techs: 'Different technologies', opens: 'Opens', blocked: 'Blocked',
            last_at: 'Last open', flag: 'Open flag', first_at: 'First open', user_id: 'User id',
            earlier_opens: 'Opens before the log',
          }}
          empty={`Nobody opened university contacts ${phrase}.`}
          render={{
            rank: r => <span className="font-bold text-grayscale-1300">{r.rank}</span>,
            person: r => <ClPersonBtn row={r} onOpen={onOpen} />,
            techs: r => <b>{Number(r.techs || 0).toLocaleString()}</b>,
            opens: r => (
              <span>
                {Number(r.opens || 0).toLocaleString()}
                {Number(r.earlier_opens) > 0 && (
                  <span className="text-grayscale-1100" title="Opened before every open was logged — from the Contact University lead records">
                    {' '}({Number(r.earlier_opens).toLocaleString()} before the log)
                  </span>
                )}
              </span>
            ),
            blocked: r => Number(r.blocked)
              ? <span className="text-red-700">{Number(r.blocked).toLocaleString()}</span>
              : <span className="text-grayscale-1100">0</span>,
            flag: r => r.open_flag ? clFlagPill(r.open_flag) : <span className="text-grayscale-1100">—</span>,
          }} />
      )}
    </Section>
  );
};

/** Grant (with the new total) or Decline one "Request more". Granting sets the
 *  account's own limit for the plan they were on when they asked. `period` is
 *  the one their count runs over, when known, for the wording. */
const ClDecisionModal: React.FC<{
  row: Record<string, any>; mode: 'grant' | 'decline'; period?: string;
  onClose: () => void; onDone: (text: string) => void;
}> = ({ row, mode, period, onClose, onDone }) => {
  const grant = mode === 'grant';
  const had = row.view_limit;
  const [limit, setLimit] = useState(
    typeof had === 'number' ? String(had + (Number(row.extra_requested) || 25)) : '');
  const [unlimited, setUnlimited] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const who = clPerson(row) || row.email || 'This person';
  const used = Number(row.used || 0);
  const typed = clParse(limit, 'New limit');
  const tooLow = grant && !unlimited && typed.value !== null && typed.value <= used;
  const win = clWindow(period, row.tier);

  const submit = async () => {
    setErr('');
    const body: Record<string, any> = {
      id: row.id, status: grant ? 'granted' : 'declined', note: note.trim(),
      // Only so the audit row names the account; the handler reads id/status/limit/note.
      user_id: row.user_id, email: row.email,
    };
    if (grant) {
      if (unlimited) body.unlimited = true;
      else if (typed.error || typed.value === null) { setErr(typed.error || 'Give the new limit, or tick Unlimited.'); return; }
      else body.limit = typed.value;
    }
    setBusy(true);
    const { error } = await panelPost('users', 'contact_limit_request_status', body);
    setBusy(false);
    if (error) { setErr(error); return; }
    onDone(grant
      ? `Granted — ${who} can now open ${unlimited ? 'unlimited' : clLimit(body.limit)} university contacts${win ? ` ${win}` : ''}.`
      : `Declined — ${who}'s limit stays as it was.`);
  };

  return (
    <Modal title={grant ? 'Grant more university contacts' : 'Decline this request'}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn kind={grant ? 'primary' : 'danger'} onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : grant ? 'Grant' : 'Decline'}
          </Btn>
        </>
      }>
      <div className="flex flex-col gap-3">
        <div className="text-sm text-grayscale-1300">
          <b>{who}</b>{row.email && clPerson(row) ? <span className="text-grayscale-1100"> · {row.email}</span> : ''}
          {' '}had opened {used.toLocaleString()} of {clLimit(had)} university contacts
          {win ? ` ${win}` : ''} and asked for{' '}
          {row.extra_requested ? `${Number(row.extra_requested).toLocaleString()} more` : 'more (no number given)'}.
        </div>
        {row.reason && (
          <div className="rounded-lg border border-grayscale-200 bg-grayscale-100 px-3 py-2 text-xs text-grayscale-1200
            whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{row.reason}</div>
        )}
        {grant && (
          <>
            <div className="flex items-end gap-3 flex-wrap">
              <Labeled label="New limit" hint="their new total, not extra on top">
                <input type="number" min={0} max={CL_MAX} step={1} value={limit} disabled={unlimited} autoFocus
                  onChange={e => setLimit(e.target.value)}
                  className="h-9 w-32 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 disabled:opacity-50" />
              </Labeled>
              <div className="pb-2"><Check checked={unlimited} onChange={setUnlimited}>Unlimited</Check></div>
            </div>
            {tooLow && (
              <div className="text-xs text-amber-700 -mt-1">
                They have already opened {used.toLocaleString()}, so this gives them no more.
              </div>
            )}
          </>
        )}
        <Labeled label="Note" hint="optional · kept with the request">
          <input value={note} maxLength={1000} onChange={e => setNote(e.target.value)} className={inputCls} />
        </Labeled>
        <div className="text-[11px] text-grayscale-1100">
          {grant
            ? <>This becomes their own limit while they are on {CL_PLAN_WORD[row.tier] || 'the plan they asked from'}, over
                the same period as before. </>
            : <>Their limit stays as it is. </>}
          They are not e-mailed about this — write to {row.email || 'them'} if you would like to let them know.
        </div>
        {err && <Msg kind="error">{err}</Msg>}
      </div>
    </Modal>
  );
};

/** The open flag on one account, at the top of its "University contacts". */
const ClFlagBanner: React.FC<{ flag: Record<string, any> }> = ({ flag: f }) => {
  const red = f.severity === 'red';
  const took = clDuration(f.first_at, f.last_at);
  return (
    <div className={`rounded-lg border px-3 py-2 text-xs flex flex-col gap-1 ${red
      ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <b className="text-sm">{red ? 'Red flag' : 'Amber flag'} — possible contact harvesting</b>
        {f.action && <Pill tone="mute">{CL_FLAG_ACTION[f.action] || f.action}</Pill>}
      </div>
      <div>
        Tried to open <b>{Number(f.peak_distinct || 0).toLocaleString()}</b> different technologies' contacts within{' '}
        {clSpan(f.window_minutes)} ({f.rule}). {clShown(f).toLocaleString()} shown,{' '}
        {Number(f.blocked || 0).toLocaleString()} blocked by their limit, {fmtDate(f.first_at)} → {fmtDate(f.last_at)}
        {took ? ` (over ${took})` : ''}.{f.source === 'history' ? ' Found by a history scan.' : ''}
      </div>
      <div>
        Pause, dismiss or act on it under{' '}
        <AdminLink to={{ group: 'users', tab: 'Contact limits' }}>Contact limits → Possible abuse</AdminLink>;
        {' '}to stop them signing in, use Suspend under Account access above.
      </div>
    </div>
  );
};

/** What one account has opened, newest first, one row per technology. */
const ClOpens: React.FC<{ userId: string; reloadKey: number }> = ({ userId, reloadKey }) => {
  const [scope, setScope] = useState<'period' | 'all'>('all');
  const [showAll, setShowAll] = useState(false);
  const opens = useAdminGet('users', 'contact_opens', { user_id: userId, scope, limit: 5000 },
    [userId, scope, reloadKey]);
  useEffect(() => { setShowAll(false); }, [userId, scope]);
  const all = useMemo(() => (opens.data?.rows || []).map((r: Record<string, any>) => ({
    ...r, title: r.title || r.utech_technology_id,
  })), [opens.data]);
  const rows = showAll ? all : all.slice(0, 10);
  const u = opens.data?.usage;
  const win = clWindow(u?.period, u?.tier);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs font-bold text-grayscale-1200 font-grotesk">
          Opened so far{opens.data ? ` (${all.length.toLocaleString()})` : ''}
        </div>
        <div className="flex gap-1">
          <Btn small kind={scope === 'period' ? 'primary' : 'subtle'} onClick={() => setScope('period')}
            title="Only what counts toward their limit now">
            This period
          </Btn>
          <Btn small kind={scope === 'all' ? 'primary' : 'subtle'} onClick={() => setScope('all')}>All time</Btn>
        </div>
        {all.length > 10 && (
          <Btn small onClick={() => setShowAll(s => !s)}>
            {showAll ? 'Show newest 10' : `Show all ${all.length.toLocaleString()}`}
          </Btn>
        )}
      </div>
      {u && (
        <div className="text-[11px] text-grayscale-1100">
          {scope === 'period'
            ? <>This period: {win || 'their current period'}{u.period_start ? `, since ${fmtDate(u.period_start)}` : ''}. </>
            : <>Everything, newest first. </>}
          "Counts now" marks the ones that count toward their limit; opening one again is free.
          {scope === 'all' ? clEarlierNote(opens.data?.log_started) : ''}
        </div>
      )}
      {opens.error && <Msg kind="error">{opens.error}</Msg>}
      {opens.loading && !opens.data ? <Loading /> : !opens.data ? null : (
        <DataTable rows={rows} tableId="contact_opens" csvName="contact_opens" maxHeight="20rem"
          columns={['title', 'university', 'first_opened', 'last_opened', 'opens', 'blocked', 'counted_now']}
          optionalColumns={['country', 'empty', 'utech_technology_id', 'page_path']}
          labels={{
            title: 'Technology', university: 'University', first_opened: 'First opened', last_opened: 'Last opened',
            opens: 'Opens', blocked: 'Blocked', counted_now: 'Counts now?', country: 'Country',
            empty: 'Opens with no contacts', utech_technology_id: 'Tech id', page_path: 'Public page',
          }}
          empty={scope === 'period' ? `Nothing opened ${win || 'this period'}.` : 'Has not opened any university contacts.'}
          render={{
            title: r => (
              <AdminLink to={{ group: 'tech_pages', tab: 'tech', target: String(r.utech_technology_id) }}
                title={`${r.title} — open in Tech pages`} className="text-primary-700 hover:underline">
                {r.title}
              </AdminLink>
            ),
            first_opened: r => fmtDate(r.first_opened),
            last_opened: r => fmtDate(r.last_opened),
            blocked: r => Number(r.blocked)
              ? <span className="text-red-700">{Number(r.blocked).toLocaleString()}</span>
              : <span className="text-grayscale-1100">0</span>,
            counted_now: r => r.counted_now ? <Pill tone="ok">yes</Pill>
              : Number(r.earlier_opens) > 0 && Number(r.earlier_opens) === Number(r.opens) + Number(r.blocked || 0)
                ? <Pill tone="mute">before the log</Pill> : <Pill tone="mute">no</Pill>,
          }} />
      )}
    </div>
  );
};

/** One account's university-contact limit, inside UserDrawer: an open flag,
 *  what they have opened in the current period, where the limit comes from,
 *  their own limit and period (set here, or by granting a request), their last
 *  few requests and everything they have opened. `reloadKey` re-reads it after
 *  a plan change elsewhere in the drawer. */
const ContactLimitSection: React.FC<{ userId: string; onChanged: () => void; reloadKey?: number }> =
  ({ userId, onChanged, reloadKey = 0 }) => {
  const acct = useAdminGet('users', 'contact_limit', { user_id: userId }, [userId, reloadKey]);
  const [tier, setTier] = useState<string>('trial');
  const [period, setPeriod] = useState('default');
  const [limit, setLimit] = useState('');
  const [unlimited, setUnlimited] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<Flash>(null);
  const [deciding, setDeciding] = useState<{ row: Record<string, any>; mode: 'grant' | 'decline' } | null>(null);
  const [opensKey, setOpensKey] = useState(0);
  const seeded = useRef<any>(null);
  const u = acct.data?.usage;

  // A deep link can swap the account under an open drawer.
  useEffect(() => { setMsg(null); setDeciding(null); }, [userId]);
  // Seed the form from each fresh answer (only a load or this section's own
  // save produces one): the current plan, and the account's own limit when it
  // is the one in effect.
  useEffect(() => {
    if (!u || seeded.current === u) return;
    seeded.current = u;
    const own = u.override && u.override.applies_now ? u.override : null;
    setTier((CL_TIERS as readonly string[]).includes(u.tier) ? u.tier : 'trial');
    setPeriod(own?.period || 'default');
    setLimit(own && own.limit !== null && own.limit !== undefined ? String(own.limit) : '');
    setUnlimited(!!own && (own.limit === null || own.limit === undefined));
    setNote(own?.note || '');
  }, [u]);

  const reload = () => { acct.reload(); setOpensKey(n => n + 1); };
  const changed = (text: string) => {
    setMsg({ kind: 'ok', text });
    reload();
    onChanged();
  };

  // The plan's own period is only known for the plan they are on now.
  const tierDefault: string | undefined = u && tier === u.tier ? u.default_period || undefined : undefined;
  const pickTier = (t: string) => {
    setTier(t);
    // "Whole trial" does not fit a paid or free limit.
    setPeriod(p => (p === 'default' || clPeriodsFor(t).includes(p) ? p : 'default'));
  };

  const save = async () => {
    setMsg(null);
    const body: Record<string, any> = {
      user_id: userId, tier, note: note.trim(), period: period === 'default' ? '' : period,
    };
    if (unlimited) body.unlimited = true;
    else {
      const p = clParse(limit, 'Limit');
      if (p.error || p.value === null) { setMsg({ kind: 'error', text: p.error || 'Give a number, or tick Unlimited.' }); return; }
      body.limit = p.value;
    }
    setBusy('save');
    const { error } = await panelPost('users', 'contact_limit_set', body);
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const per = period !== 'default' ? period : tierDefault;
    changed(`Saved — ${unlimited ? 'unlimited' : clLimitIn(body.limit, per)} university contacts`
      + `${!unlimited && !per ? " over the plan's usual period" : ''}, while they are on ${CL_PLAN_WORD[tier] || tier}.`);
  };

  const clear = async () => {
    setMsg(null);
    setBusy('clear');
    const { error } = await panelPost('users', 'contact_limit_set', { user_id: userId, clear: true });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    changed('Back to the default for their plan.');
  };

  const own = u?.override;
  const reqs: Record<string, any>[] = (acct.data?.requests || []).slice(0, 5);
  const flags: Record<string, any>[] = acct.data?.flags || [];
  const openFlag = flags.find(f => f.status === 'open');
  const pastFlags = flags.filter(f => f.status !== 'open');
  const lastView = (acct.data?.recent_views || [])[0]?.first_viewed_at;
  const isTeam = u?.tier === 'admin';
  const atLimit = u && u.limit !== null && u.limit !== undefined && Number(u.used) >= Number(u.limit);
  const periodOptions = ['default', ...clPeriodsFor(tier)];
  const periodLabels: Record<string, string> = {
    ...CL_PERIOD_LABEL,
    default: tierDefault ? `Plan default (${(CL_PERIOD_LABEL[tierDefault] || tierDefault).toLowerCase()})` : 'Plan default',
  };
  // When the count starts again: a trial's ends with the trial.
  const resets = !u ? null
    : u.period === 'trial' ? (u.resets_at ? <> · trial ends {fmtDate(u.resets_at)}</> : null)
      : u.resets_at ? <> · resets {fmtDate(u.resets_at)}</>
        : u.period === 'lifetime' ? <> · never resets</> : null;

  return (
    <Section title="University contacts" right={<Btn small onClick={reload}>Refresh</Btn>}>
      {acct.error && <Msg kind="error">{acct.error}</Msg>}
      {openFlag && <ClFlagBanner flag={openFlag} />}
      {!openFlag && pastFlags.length > 0 && (
        <div className="text-[11px] text-grayscale-1100">
          Flagged before: {pastFlags.length} time{pastFlags.length === 1 ? '' : 's'}, most recently{' '}
          {fmtDate(pastFlags[0].created_at)} ({pastFlags[0].severity}, {pastFlags[0].status}
          {pastFlags[0].action ? ` — ${CL_FLAG_ACTION[pastFlags[0].action] || pastFlags[0].action}` : ''}).
        </div>
      )}
      {acct.loading && !u ? <Loading /> : !u ? null : isTeam ? (
        <div className="text-sm text-grayscale-1200">
          Team account — never limited.
          {lastView && <span className="text-xs text-grayscale-1100"> Last opened {fmtDate(lastView)}.</span>}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2 flex-wrap text-sm text-grayscale-1300">
              <span>
                <b>{Number(u.used || 0).toLocaleString()}</b> of <b>{clLimit(u.limit)}</b>{' '}
                {clWindow(u.period, u.tier) || u.window_label}
              </span>
              {atLimit && <Pill tone="bad">limit reached</Pill>}
              {u.pending_request && <Pill tone="warn">asked for more</Pill>}
            </div>
            <div className="text-xs text-grayscale-1100">
              {u.source === 'account'
                ? "This account's own limit"
                : `Default for ${(CL_TIER_LABEL[u.tier] || u.tier).toLowerCase()} (${clLimitIn(u.default_limit, u.default_period)})`}
              {u.free_locked ? ' — the Free-tier lock is on, so none' : ''}
              {' · '}on {CL_PLAN_WORD[u.tier] || u.tier}
              {u.state ? ` (${LIFECYCLE_LABEL[toLifecycle(u.state)] || String(u.state).replace(/_/g, ' ')})` : ''}
              {resets}
              {lastView && <> · last opened {fmtDate(lastView)}</>}
            </div>
          </div>

          {own && (
            <div className="text-xs rounded-lg border border-grayscale-200 bg-grayscale-100 px-3 py-2 flex flex-col gap-0.5">
              <div className="flex items-center gap-2 flex-wrap text-grayscale-1300">
                <span>
                  Own limit: <b>{own.period ? clLimitIn(own.limit, own.period) : clLimit(own.limit)}</b>
                  {!own.period && own.limit !== null && own.limit !== undefined ? " over the plan's usual period" : ''}
                  {' '}on {CL_PLAN_WORD[own.tier] || own.tier}
                </span>
                {!own.applies_now && <Pill tone="warn">not in effect now</Pill>}
              </div>
              {!own.applies_now && (
                <div className="text-grayscale-1100">
                  It only counts while they are on {CL_PLAN_WORD[own.tier] || own.tier}; they are on{' '}
                  {CL_PLAN_WORD[u.tier] || u.tier} now, so that plan's default is used.
                </div>
              )}
              <div className="text-grayscale-1100 break-words">
                Set by {own.set_by || 'unknown'} · {fmtDate(own.set_at)}
                {own.note ? <> · <span className="text-grayscale-1200">{own.note}</span></> : ''}
              </div>
            </div>
          )}

          <div className="flex items-end gap-3 flex-wrap">
            <Labeled label="Plan it applies to">
              <Select value={tier} onChange={pickTier} options={[...CL_TIERS]} labels={CL_TIER_LABEL} />
            </Labeled>
            <Labeled label="Limit">
              <input type="number" min={0} max={CL_MAX} step={1} value={limit} disabled={unlimited}
                placeholder={unlimited ? 'unlimited' : 'e.g. 100'} onChange={e => setLimit(e.target.value)}
                className="h-9 w-28 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 disabled:opacity-50" />
            </Labeled>
            <Labeled label="Counted over">
              <Select value={period} onChange={setPeriod} options={periodOptions} labels={periodLabels} />
            </Labeled>
            <div className="pb-2"><Check checked={unlimited} onChange={setUnlimited}>Unlimited</Check></div>
          </div>
          <Labeled label="Note" hint="optional · why this account is different">
            <input value={note} maxLength={500} onChange={e => setNote(e.target.value)} className={inputCls} />
          </Labeled>
          <div className="flex items-center gap-2 flex-wrap">
            <Btn small kind="primary" onClick={save} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save limit'}</Btn>
            {own && (
              <Btn small onClick={clear} disabled={!!busy}
                title="Remove this account's own limit — the default for their plan applies">
                {busy === 'clear' ? 'Resetting…' : 'Back to default'}
              </Btn>
            )}
            <span className="text-[11px] text-grayscale-1100">
              Only counts while they are on the plan picked here; one own limit per account.
            </span>
          </div>
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

          {reqs.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-xs font-bold text-grayscale-1200 font-grotesk">Requests for more</div>
              <div className="flex flex-col divide-y divide-grayscale-200">
                {reqs.map((r, i) => (
                  <div key={r.id ?? i} className="py-1.5 flex flex-col gap-0.5 text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                      {clStatusPill(r)}
                      <span className="text-grayscale-1100">
                        {fmtDate(r.created_at)} · had opened {Number(r.used || 0).toLocaleString()} of {clLimit(r.view_limit)}
                        {' '}· asked for {r.extra_requested ? `${Number(r.extra_requested).toLocaleString()} more` : 'more'}
                      </span>
                      {r.status === 'open' && (
                        <span className="inline-flex gap-1">
                          <Btn small kind="primary" onClick={() => setDeciding({ row: r, mode: 'grant' })}>Grant…</Btn>
                          <Btn small onClick={() => setDeciding({ row: r, mode: 'decline' })}>Decline…</Btn>
                        </span>
                      )}
                    </div>
                    {r.reason && <div className="text-grayscale-1200 truncate" title={r.reason}>{r.reason}</div>}
                    {r.status !== 'open' && (r.handled_by || r.admin_note) && (
                      <div className="text-grayscale-1100 break-words">
                        {r.handled_by || '?'} · {fmtDate(r.handled_at)}{r.admin_note ? ` · ${r.admin_note}` : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {u && <ClOpens userId={userId} reloadKey={reloadKey + opensKey} />}
      {deciding && (
        <ClDecisionModal row={deciding.row} mode={deciding.mode}
          period={u && deciding.row.tier === u.tier ? u.period : undefined}
          onClose={() => setDeciding(null)}
          onDone={text => { setDeciding(null); changed(text); clAlertsChanged(); }} />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const FeedbackBugs: React.FC = () => {
  const overview = useAdminGet('users', 'requests_overview');
  const [rating, setRating] = useState('All');
  const feedback = useAdminGet('users', 'feedback', { rating }, [rating]);
  const bugs = useAdminGet('users', 'bugs');
  const [msg, setMsg] = useState<Flash>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const saveBug = async (id: string, status?: string) => {
    const { error } = await panelPost('users', 'bug_update',
      { bug_id: id, status, admin_notes: notes[id] });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: 'Bug updated.' }); bugs.reload(); }
  };

  const deleteBug = async (id: string) => {
    const { data, error } = await panelPost('users', 'bug_delete', { bug_id: id });
    if (error) setMsg({ kind: 'error', text: error });
    else {
      setMsg({ kind: data?.deleted ? 'ok' : 'error',
               text: data?.deleted ? 'Bug report deleted.' : 'That report no longer exists.' });
      bugs.reload();
      overview.reload();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {overview.data && (
        <KPIRow>
          <KPI label="Chat feedback" value={overview.data.feedback} />
          <KPI label="Bug reports" value={overview.data.bugs} />
          <KPI label="Open concierge" value={overview.data.concierge} hint="managed under Leads" />
          <KPI label="Invites" value={overview.data.invites} />
        </KPIRow>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="Chat feedback"
        right={<Select value={rating} onChange={setRating} options={['All', '1-2', '4-5']}
          labels={{ 'All': 'All ratings', '1-2': 'Low (1–2)', '4-5': 'High (4–5)' }} />}>
        {feedback.loading ? <Loading /> : (
          <DataTable rows={feedback.data || []} searchable csvName="chat_feedback" />
        )}
      </Section>
      <Section title="Bug reports">
        {bugs.loading ? <Loading /> : (
          <DataTable rows={bugs.data || []} searchable csvName="bug_reports"
            columns={['email', 'bug_category', 'bug_description', 'status', 'admin_notes', 'created_at']}
            actions={r => (
              <div className="flex gap-1 items-center">
                <input value={notes[r.id] ?? (r.admin_notes || '')} placeholder="Notes…"
                  onChange={e => setNotes(s => ({ ...s, [r.id]: e.target.value }))}
                  className="h-7 px-2 rounded border-[0.5px] border-primary-300 text-xs w-32" />
                {['Open', 'In progress', 'Fixed'].filter(s => s !== r.status).map(s => (
                  <Btn key={s} small onClick={() => saveBug(r.id, s)}>{s}</Btn>
                ))}
                <DangerBtn small onConfirm={() => deleteBug(r.id)}>Delete</DangerBtn>
              </div>
            )} />
        )}
      </Section>
    </div>
  );
};

export default UsersPanel;
